import { OrderRefusalDialogComponent } from '../refusal/order-refusal-dialog.component';
import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import { MatDrawer } from '@angular/material/sidenav';
import { MatTooltip } from '@angular/material/tooltip';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, firstValueFrom, merge, Observable, of, Subscription } from 'rxjs';
import { catchError, debounceTime, map, scan, shareReplay, startWith, switchMap, tap, timeout } from 'rxjs/operators';
import { CartService } from 'src/app/cart/cart.service';
import { ClientService } from 'src/app/client/client.service';
import { getClientPrimaryPhone } from 'src/app/client/client-phone.util';
import { CreateCarComponent } from 'src/app/client/create-car/create-car.component';
import { DeviceTypeEnum, DocTypeEnum } from 'src/enums';
import { ProductService } from 'src/app/product/product.service';
import { SelectorComponent } from 'src/app/product/selector/selector.component';
import { AppState } from 'src/app/redux/app.state';
import { NotificationService } from 'src/app/services/notification.service';
import { UserService } from 'src/app/services/user.service';
import { SupportService } from 'src/app/support.service';
import {
  Order,
  Product,
  ProductInDoc,
  ProductInProviderWarehouse,
  ProductWithControls,
  Provider,
  Client,
  ClientCar,
  User,
  NovaPoshtaCity,
  NovaPoshtaDeliveryKind,
  NovaPoshtaWarehouse,
  OrderDelivery,
  OrderDeliveryMethod,
  ContractWithProviderDoc
} from 'src/typings';
import {
  OrderService,
  OrderSupplyAllocation,
  OrderSupplyLine,
  OrderTransferSource,
  OrderSupplyResponse
} from '../order.service';
import { NovaPoshtaService } from '../nova-poshta.service';
import { calculateOrderReadiness } from '../order-readiness.util';
import { clearCart, removeFromCart } from 'src/app/redux/actions/cart.actions';
import { MarkupService } from 'src/app/markup-table/markup.service';
import {
  getOrderPaymentStatusLabel,
  getOrderShipmentStatusLabel,
  getOrderSupplyStatusLabel
} from '../order-status.util';
import { UnavailableProductsDialogComponent } from '../unavailable-products-dialog/unavailable-products-dialog.component';
import { environment } from 'src/environments/environment';
import {
  PartsSelectionWarehouseOption,
  PartsSelectionOfferSupplierOption,
  PartsSelectionService
} from 'src/app/parts-selection/parts-selection.service';
import {
  allocateAvailableWarehouseStock,
  findWarehouseStockConflicts,
  WarehouseStockConflict
} from 'src/app/parts-selection/workspace/warehouse-allocation.util';
import { orderSupplyCoverage, orderSupplierReceipt, OrderSaveRequestIdentity } from './order-warehouse-allocation.util';
import { extractApiErrorMessage } from 'src/app/shared/utils/extract-api-error-message.util';
import { formatKyivDateTime, kyivWallTime } from 'src/app/shared/utils/kyiv-date-time.util';
import { OrderSupplierCancellationService } from '../supplier-cancellation/order-supplier-cancellation.service';
import { SupplierCancellationDialogComponent } from '../supplier-cancellation/supplier-cancellation-dialog.component';
import { OrderTransferCancellationService } from '../transfer-cancellation/order-transfer-cancellation.service';
import { TransferCancellationDialogComponent } from '../transfer-cancellation/transfer-cancellation-dialog.component';
import { readCartNavigationSelection } from 'src/app/cart/cart-navigation-state.util';

type PartsSelectionOrderLink = {
  id: number;
  label: string;
};

type OrderProductPriceSource = {
  basePrice: number;
  provider?: Provider;
};

type SupplySourceSummary = {
  label: string;
  quantity: string;
  status: string;
  icon: string;
  kind: 'warehouse' | 'supplier' | 'transfer' | 'shipped';
  details?: string;
  deliveryLabel?: string;
  deliveryDetails?: string;
  deliveryEstimated?: boolean;
};

@Component({
  selector: 'app-update',
  templateUrl: './update.component.html',
  styleUrls: ['./update.component.scss']
})
export class UpdateComponent implements OnInit, OnDestroy {
  id: number | undefined;

  order: Order | undefined;

  supply: OrderSupplyResponse | undefined;

  supplyForms = new Map<AbstractControl, UntypedFormArray>();

  private readonly expandedSupplyRows = new WeakSet<AbstractControl>();

  warehouseForms = new Map<AbstractControl, UntypedFormArray>();
  warehouseOptions: PartsSelectionWarehouseOption[] = [];
  warehouseOptionsLoading = false;
  warehouseOptionsError = '';
  warehouseCheckedAt = '';
  private warehouseSourcesChanged = false;
  warehousePickerRow: AbstractControl | null = null;
  warehouseConflicts: WarehouseStockConflict[] = [];
  private readonly initializedWarehouseRows = new WeakSet<AbstractControl>();
  private readonly editedSourceRows = new WeakSet<AbstractControl>();
  private readonly pendingStockReleases = new Map<AbstractControl, {
    warehouse_id: number; quantity: number; warehouse_name: string; picked: number;
  }>();
  private readonly saveRequestIdentity = new OrderSaveRequestIdentity();
  private warehouseOptionsVersion = 0;
  private warehouseContextSubscription?: Subscription;

  readonly supplyOrgControl = new UntypedFormControl();

  private readonly supplyProviderOptionsByContext = new Map<
    string,
    PartsSelectionOfferSupplierOption[]
  >();

  private readonly loadingSupplyProviderOptionContexts = new Set<string>();

  private readonly supplyProviderOptionsRequests = new Map<
    string,
    Promise<PartsSelectionOfferSupplierOption[]>
  >();

  private readonly draftSupplySuggestionVersions = new WeakMap<AbstractControl, number>();

  private readonly automaticSupplyTimers = new Map<AbstractControl, ReturnType<typeof setTimeout>>();
  private readonly automaticSupplyPendingRows = new Set<AbstractControl>();
  private automaticSupplyQueue: Promise<void> = Promise.resolve();
  private automaticSupplyDisposed = false;

  private readonly supplierPriceRequests = new Map<AbstractControl, { timer?: ReturnType<typeof setTimeout> }>();
  private readonly supplierPriceErrors = new Set<AbstractControl>();
  private readonly manuallyPricedRows = new WeakSet<AbstractControl>();

  supplyLoading = false;

  supplySaving = false;

  user: User | undefined;

  deviceWidth = 0;

  cartProducts: ProductWithControls[] = [];

  deviceType: DeviceTypeEnum = DeviceTypeEnum.MOBILE;

  routeSubscription: Subscription;

  storeSubscription: Subscription;

  passedProducts?: ProductWithControls[];
  private cartSelectionAllowed = false;
  private cartSelectionApplied = false;

  saving = false;

  removeConfirmationPending = false;
  removedProductsCount = 0;

  @ViewChild('removeProductsDialog') removeProductsDialog!: TemplateRef<unknown>;

  lastFiscalEditConflict = false;

  private pendingProductIds = new Set<number>();

  readonly orderDeliveryMethod = OrderDeliveryMethod;

  readonly novaPoshtaDeliveryKind = NovaPoshtaDeliveryKind;

  filteredNovaPoshtaCities$: Observable<NovaPoshtaCity[]> = of([]);

  filteredNovaPoshtaWarehouses$: Observable<NovaPoshtaWarehouse[]> = of([]);

  novaPoshtaWarehouseResults: NovaPoshtaWarehouse[] = [];

  isNovaPoshtaWarehouseListOpen = false;

  novaPoshtaWarehouseActiveIndex = -1;

  private novaPoshtaWarehouseLookupSubscription?: Subscription;

  readonly productImagePreviewSizePx = 260;

  readonly productImagePreviewOffsetPx = 18;

  hoveredProductImageUrl: string | null = null;

  hoveredProductImageTitle = '';

  hoveredProductImagePreviewLeft = 0;

  hoveredProductImagePreviewTop = 0;

  private readonly failedProductImageUrls = new Set<string>();
  private readonly failedProductBrandLogoUrls = new Set<string>();

  @ViewChild('table') table: any;

  @ViewChild('novaPoshtaWarehouseInput')
  private novaPoshtaWarehouseInput?: ElementRef<HTMLInputElement>;

  @ViewChild('novaPoshtaWarehouseTrigger')
  private novaPoshtaWarehouseTrigger?: MatAutocompleteTrigger;

  @ViewChildren('novaPoshtaWarehouseOption')
  private novaPoshtaWarehouseOptions?: QueryList<ElementRef<HTMLButtonElement>>;

  @ViewChild('orderEditor') editorRoot?: ElementRef<HTMLElement>;
  @ViewChild('drawer') editorDrawer?: MatDrawer;
  @ViewChild('keyboardHelpTooltip') keyboardHelpTooltip?: MatTooltip;
  @ViewChild('addProductButton', { read: ElementRef }) addProductButton?: ElementRef<HTMLButtonElement>;

  activeProductIndex = 0;
  private productDialogOpen = false;
  private destroyed = false;
  private focusTimer?: ReturnType<typeof setTimeout>;
  private productDialogSubscriptions = new Subscription();

  readonly keyboardHelpText = 'Заказ без мышки\n\n' +
    'F2 — найти и добавить товар\n' +
    'Enter / Shift+Enter — цена → количество → следующий товар / назад\n' +
    '↑ / ↓ — предыдущий / следующий товар в том же столбце\n' +
    'Tab / Shift+Tab — следующее / предыдущее поле\n' +
    'Alt+1 — клиент\nAlt+2 — автомобиль\nAlt+3 — обеспечение текущего товара\n' +
    'Alt+4 — склад\nAlt+5 — способ получения\nAlt+6 — комментарий\n' +
    'Alt+9 — вернуться к цене текущего товара\n' +
    'Ctrl+S / Ctrl+Enter — сохранить заказ\n' +
    'Esc — предыдущая страница; в открытом окне или списке — закрыть его\n' +
    'Alt+0 — показать / скрыть памятку\n\n' +
    'В подборе: артикул → Enter → ↑/↓ → Enter. После выбора — цена товара. ' +
    'В последней строке Enter после количества открывает поиск следующего товара. ' +
    'В списках: введите название → ↓ → Enter. Сочетания работают и в русской раскладке.';

  private hasKeyboardOverlay(): boolean {
    return Boolean(this.productDialogOpen || this.dialog.openDialogs?.length ||
      this.isNovaPoshtaWarehouseListOpen ||
      document.querySelector('.cdk-overlay-container .mat-datepicker-content, .cdk-overlay-container .mat-select-panel, .cdk-overlay-container .mat-autocomplete-panel, .cdk-overlay-container .mat-menu-panel'));
  }

  private focusKeyboardElement(element: HTMLElement | null): boolean {
    if (!element || element.matches(':disabled, [readonly], [aria-disabled="true"]')) return false;
    element.focus();
    if (element.matches('input, textarea')) {
      try { (element as HTMLInputElement).select(); } catch { /* Native input may not support selection. */ }
    }
    element.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    return true;
  }

  async focusOrderField(field: string): Promise<void> {
    await this.editorDrawer?.open();
    if (this.destroyed || this.hasKeyboardOverlay()) return;
    const container = this.editorRoot?.nativeElement.querySelector<HTMLElement>(`[data-order-focus="${field}"]`);
    if (!container) return;
    this.focusKeyboardElement(container.matches('input, textarea') ? container :
      container.querySelector<HTMLElement>('button[aria-pressed="true"], input:not([disabled]), textarea:not([disabled]), button:not([disabled])'));
  }

  focusProductCell(index = this.activeProductIndex, field = 'price'): boolean {
    if (!this.products.length) return false;
    const rowIndex = Math.max(0, Math.min(index, this.products.length - 1));
    const focused = this.focusKeyboardElement(this.editorRoot?.nativeElement.querySelector<HTMLInputElement>(
      `input[data-order-row="${rowIndex}"][data-order-field="${field}"]`
    ) || null);
    if (focused) this.activeProductIndex = rowIndex;
    return focused;
  }

  private focusProductsAfterRender(index: number) {
    if (this.focusTimer) clearTimeout(this.focusTimer);
    this.focusTimer = setTimeout(() => {
      if (this.destroyed || this.hasKeyboardOverlay()) return;
      if (!this.focusProductCell(index)) this.focusKeyboardElement(this.addProductButton?.nativeElement || null);
    }, 0);
  }

  private focusSupply(): void {
    const row = this.products[Math.min(this.activeProductIndex, this.products.length - 1)];
    if (!row) { this.openDialog(); return; }
    this.expandedSupplyRows.add(row);
    setTimeout(() => {
      if (this.destroyed) return;
      const cell = this.editorRoot?.nativeElement.querySelector<HTMLElement>(
        `[data-supply-product-id="${Number(row.value.product.id)}"]`
      );
      this.focusKeyboardElement(cell?.querySelector<HTMLElement>(
        '.supplyEditorBody input:not([disabled]):not([readonly]), .supplyEditorBody button:not([disabled]), .supplyEditorBody a[href]'
      ) || cell?.querySelector<HTMLElement>('.supplyDisclosure') || null);
    }, 0);
  }

  @HostListener('document:keydown', ['$event'])
  onOrderEditorKeydown(event: KeyboardEvent): void {
    if (this.destroyed || !this.editorRoot || event.defaultPrevented || event.isComposing ||
      event.getModifierState?.('AltGraph')) return;
    const plain = !event.ctrlKey && !event.metaKey && !event.altKey;
    const command = (event.ctrlKey || event.metaKey) && !event.altKey && !event.shiftKey;
    const digit = event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey
      ? /^(?:Digit|Numpad)([0-9])$/.exec(event.code)?.[1] : undefined;
    const fields: Record<string, string> = { '1': 'client', '2': 'car', '4': 'warehouse', '5': 'delivery', '6': 'comment' };
    const add = plain && !event.shiftKey && event.key === 'F2';
    const save = command && (event.code === 'KeyS' || event.key === 'Enter');
    const shortcut = digit !== undefined && (digit in fields || ['0', '3', '9'].includes(digit));
    if (add || save || shortcut) {
      event.preventDefault();
      if (event.repeat || this.saving || this.supplySaving || this.hasKeyboardOverlay()) return;
      if (add) { this.openDialog(); return; }
      if (save) {
        if (this.pendingProductIds.size) return;
        if (!this.warehouse?.id) {
          void this.focusOrderField('warehouse');
          this.notificationService.warning('Выберите склад из списка');
          return;
        }
        void this.save();
        return;
      }
      if (digit === '0') this.keyboardHelpTooltip?.toggle();
      else if (digit === '3') this.focusSupply();
      else if (digit === '9') {
        if (!this.focusProductCell()) this.openDialog();
      } else void this.focusOrderField(fields[digit!]);
      return;
    }
    if (!plain || this.hasKeyboardOverlay() || this.saving || this.supplySaving) return;
    const target = event.target as HTMLElement | null;
    if (!target || !this.editorRoot.nativeElement.contains(target)) return;
    if (event.key === 'Escape' && !event.shiftKey) {
      this.keyboardHelpTooltip?.hide(0);
      return; // Unconsumed Escape is handled by the global page-back shortcut.
    }
    const field = target.getAttribute('data-order-field');
    const rowAttribute = target.getAttribute('data-order-row');
    const index = Number(rowAttribute);
    if (['price', 'quan', 'sum'].includes(field || '') && rowAttribute !== null && Number.isInteger(index)) {
      let nextIndex = index;
      let nextField = field!;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (event.shiftKey) return;
        nextIndex += event.key === 'ArrowDown' ? 1 : -1;
      } else if (event.key === 'Enter') {
        if (event.shiftKey) {
          if (field === 'price') { nextIndex--; nextField = 'quan'; }
          else nextField = 'price';
        } else if (field === 'price') nextField = 'quan';
        else { nextIndex++; nextField = 'price'; }
      } else return; // Tab retains the native path through all fields and actions.
      event.preventDefault();
      if (event.repeat && event.key === 'Enter') return;
      if (nextIndex >= this.products.length && event.key === 'Enter' && !event.shiftKey) this.openDialog();
      else this.focusProductCell(nextIndex, nextField);
      return;
    }
    // Enter in ordinary fields must not submit the document; multiline comments and buttons retain Enter.
    if (event.key === 'Enter' && target.matches('input') && !target.closest('button, a, [role="button"]'))
      event.preventDefault();
  }

  constructor(
    public dialog: MatDialog,
    private fb: UntypedFormBuilder,
    private orderService: OrderService,
    private userService: UserService,
    private router: Router,
    private activateRoute: ActivatedRoute,
    private clientService: ClientService,
    public supportService: SupportService,
    private notificationService: NotificationService,
    private store: Store<AppState>,
    private cartService: CartService,
    private productService: ProductService,
    private titleService: Title,
    private markupService: MarkupService,
    private novaPoshtaService: NovaPoshtaService,
    private partsSelectionService: PartsSelectionService,
    private supplierCancellation: OrderSupplierCancellationService,
    private transferCancellation: OrderTransferCancellationService
  ) {
    this.tomorrow.setDate(this.tomorrow.getDate() + 1);
    this.titleService.setTitle('Создать заказ');
    this.productService.productGw.subscribe((list) => {
      for (let index = 0; index < list.length; index++) {
        const product = list[index];
        const find = this.productsFormArray.controls.find(
          (prod) => prod.value.product.id === product.id
        );
        if (find) find.setValue({ ...find.value, product: product });
      }
    });
    this.passedProducts = readCartNavigationSelection(this.router);
    this.storeSubscription = this.store.subscribe((data) => {
      this.user = data.user.info || undefined;
      this.supplyOrgControl.setValue(this.user?.defaultOrg || null, { emitEvent: false });
      if (!this.id && !this.warehouseControl.value && this.user?.defaultWarehouse) {
        this.warehouseControl.setValue(this.user.defaultWarehouse);
      }
      this.cartProducts = data.cart;
      this.deviceType = data.user.deviceType;
      void this.loadBySelected();
    });
    this.routeSubscription = activateRoute.params.subscribe((params) => {
      this.id = params.id;
      this.cartSelectionAllowed = !this.id;
      if (this.id) {
        this.passedProducts = undefined;
        this.orderService.getOne(params.id).subscribe((order) => {
          this.order = order;
          this.loadDoc();
          void this.loadSupply();
        });
      } else {
        void this.loadBySelected();
      }
    });
  }

  displayedColumns: string[] = [
    'position',
    'description',
    'price',
    'quan',
    'sum',
    'provider',
    'action'
  ];

  dataSource = new BehaviorSubject<AbstractControl[]>([]);

  tomorrow = new Date();

  createForm: UntypedFormGroup = this.fb.group({
    products: this.fb.array([]),
    client: new UntypedFormControl(),
    description: new UntypedFormControl(),
    clientCar: new UntypedFormControl(),
    warehouse: new UntypedFormControl(),
    dateExpected: new UntypedFormControl(this.tomorrow),
    timeExpected: new UntypedFormControl('12:00'),
    deliveryMethod: new UntypedFormControl(OrderDeliveryMethod.PICKUP),
    novaPoshtaKind: new UntypedFormControl(NovaPoshtaDeliveryKind.WAREHOUSE),
    novaPoshtaCity: new UntypedFormControl(),
    novaPoshtaWarehouse: new UntypedFormControl(),
    novaPoshtaRecipientFirstName: new UntypedFormControl(),
    novaPoshtaRecipientLastName: new UntypedFormControl(),
    novaPoshtaRecipientPhone: new UntypedFormControl()
  });

  bracodeControl: UntypedFormControl = new UntypedFormControl('');

  selectedProducts: ProductInDoc[] = [];

  get dateExpected() {
    return this.createForm.controls.dateExpected.value;
  }

  get timeExpected() {
    return this.createForm.controls.timeExpected.value;
  }

  get dateExpectedFormControl(): UntypedFormControl {
    return this.createForm.controls.dateExpected as UntypedFormControl;
  }

  get timeExpectedFormControl(): UntypedFormControl {
    return this.createForm.controls.timeExpected as UntypedFormControl;
  }

  get hasPendingWarehouseTransfer() {
    if (this.warehouseChangePending) return true;
    return this.productsFormArray.controls.some((row) => {
      const line = this.getSupplyLine(row);
      const pending =
        line?.transfer_pending ??
        Number(line?.transfer_reserved || 0) + Number(line?.transfer_in_transit || 0);
      return (
        pending > 0 ||
        this.serializeWarehouseAllocations(row).some(
          (source) => source.quantity > 0 && source.warehouse_id !== Number(this.warehouse?.id)
        )
      );
    });
  }

  get editableOrderReadiness() {
    const targetWarehouseId = Number(this.warehouse?.id || 0);
    return calculateOrderReadiness(this.productsFormArray.controls.map(row => {
      const line = this.getEditableSupplyLine(row);
      const stockToTransfer = this.warehouseChangePending ? Number(line?.reserved || 0) : 0;
      const warehouses = this.serializeWarehouseAllocations(row).filter(source =>
        source.warehouse_id > 0 && Number.isFinite(source.quantity) && source.quantity > 0);
      return {
        required: Number(row.value.quan || 0),
        reserved: Number(line?.reserved || 0) - stockToTransfer + warehouses.filter(source => source.warehouse_id === targetWarehouseId)
          .reduce((sum, source) => sum + source.quantity, 0),
        fulfilled: Number(line?.fulfilled || 0),
        transfer_pending: Number(line?.transfer_pending ??
          Number(line?.transfer_reserved || 0) + Number(line?.transfer_in_transit || 0)) + stockToTransfer +
          warehouses.filter(source => source.warehouse_id !== targetWarehouseId)
            .reduce((sum, source) => sum + source.quantity, 0),
        allocations: [
          ...(line?.allocations || []).filter(source => source.status !== 'PLANNED'),
          ...this.serializeSupplyAllocations(row).map(source => ({ ...source, status: 'PLANNED' }))
        ]
      };
    }));
  }

  get calculatedOrderExpectedAtCaption(): string {
    if (this.supplyLoading) return 'Проверяем источники обеспечения';
    if (this.automaticSupplyPending) return 'Подбираем склады и поставщиков';
    if (this.warehouseChangePending) return 'Обеспечение и готовность пересчитаются при сохранении заказа';
    switch (this.editableOrderReadiness.status) {
      case 'UNSOURCED': return 'Выберите источники для всех оставшихся товаров';
      case 'TRANSFER_PENDING': return 'Срок готовности подтвердим после согласования перемещения';
      case 'DATE_MISSING': return 'Укажите срок поставки у всех поставщиков';
      case 'IN_STOCK': return 'Поставка не требуется';
      case 'EMPTY': return 'Добавьте товары и выберите источники';
      default: return 'Автоматически по самой поздней поставке позиции';
    }
  }

  get calculatedOrderExpectedAt(): Date | null {
    return this.warehouseChangePending ? null : this.getLatestSupplyExpectedAt();
  }

  get calculatedOrderExpectedAtLabel(): string {
    if (this.supplyLoading) return 'Рассчитываем…';
    if (this.automaticSupplyPending) return 'Подбираем обеспечение…';
    if (this.warehouseChangePending) return 'После сохранения';
    const value = this.calculatedOrderExpectedAt;
    if (!value) return this.editableOrderReadiness.status === 'IN_STOCK' ? 'На складе' : 'Срок уточняется';
    const day = String(value.getDate()).padStart(2, '0');
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const hours = String(value.getHours()).padStart(2, '0');
    const minutes = String(value.getMinutes()).padStart(2, '0');
    return `${day}.${month}.${value.getFullYear()} ${hours}:${minutes}`;
  }

  get warehouse() {
    return this.createForm.controls.warehouse.value;
  }

  get warehouseControl() {
    return this.createForm.controls.warehouse as UntypedFormControl;
  }

  get warehouseChangePending(): boolean {
    return Boolean(this.id && this.order?.warehouse?.id && this.warehouse?.id &&
      Number(this.order.warehouse.id) !== Number(this.warehouse.id));
  }

  get client() {
    return this.createForm.controls.client.value;
  }

  get clientFormControl(): UntypedFormControl {
    return this.createForm.controls.client as UntypedFormControl;
  }

  get clientCar(): ClientCar | null {
    const value = this.createForm.controls.clientCar.value;
    return value && typeof value === 'object' ? (value as ClientCar) : null;
  }

  get clientCarFormControl(): UntypedFormControl {
    return this.createForm.controls.clientCar as UntypedFormControl;
  }

  get filteredClientCars(): ClientCar[] {
    const cars: ClientCar[] = this.client?.cars || [];
    const value = this.clientCarFormControl.value;
    const query = typeof value === 'string' ? this.normalizeClientCarSearch(value) : '';

    if (!query) {
      return cars;
    }

    return cars.filter((car) => {
      const description = this.normalizeClientCarSearch(car.description);
      const vin = this.normalizeClientCarSearch(car.vin);
      return description.includes(query) || vin.includes(query);
    });
  }

  readonly displayClientCar = (value: ClientCar | string | null): string => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    return [value.description || 'Без названия', value.vin || ''].filter(Boolean).join(' | ');
  };

  trackByClientCar(_: number, car: ClientCar) {
    return car.id;
  }

  get description() {
    return this.createForm.controls.description.value;
  }

  get descriptionFormControl(): UntypedFormControl {
    return this.createForm.controls.description as UntypedFormControl;
  }

  get deliveryMethodControl(): UntypedFormControl {
    return this.createForm.controls.deliveryMethod as UntypedFormControl;
  }

  get novaPoshtaKindControl(): UntypedFormControl {
    return this.createForm.controls.novaPoshtaKind as UntypedFormControl;
  }

  get novaPoshtaCityControl(): UntypedFormControl {
    return this.createForm.controls.novaPoshtaCity as UntypedFormControl;
  }

  get novaPoshtaWarehouseControl(): UntypedFormControl {
    return this.createForm.controls.novaPoshtaWarehouse as UntypedFormControl;
  }

  get novaPoshtaRecipientFirstNameControl(): UntypedFormControl {
    return this.createForm.controls.novaPoshtaRecipientFirstName as UntypedFormControl;
  }

  get novaPoshtaRecipientLastNameControl(): UntypedFormControl {
    return this.createForm.controls.novaPoshtaRecipientLastName as UntypedFormControl;
  }

  get novaPoshtaRecipientPhoneControl(): UntypedFormControl {
    return this.createForm.controls.novaPoshtaRecipientPhone as UntypedFormControl;
  }

  get selectedNovaPoshtaCity(): NovaPoshtaCity | null {
    const value = this.novaPoshtaCityControl.value;
    return value && typeof value === 'object' ? value : null;
  }

  get partsSelectionLink(): PartsSelectionOrderLink | null {
    const value = String(this.description || '').trim();
    const match = value.match(/^Документ подбора #(\d+)$/);
    if (!match) {
      return null;
    }

    return {
      id: Number(match[1]),
      label: value
    };
  }

  get products() {
    return (this.createForm.get('products') as UntypedFormArray).controls;
  }

  get productsInDoc() {
    return this.createForm.controls.products.value;
  }

  get productsFormArray() {
    return this.createForm.get('products') as UntypedFormArray;
  }

  ngOnInit() {
    this.setupNovaPoshtaLookups();
    this.warehouseContextSubscription = this.warehouseControl.valueChanges.subscribe(() => {
      this.warehouseOptions = [];
      this.warehouseCheckedAt = '';
      if (this.productsFormArray.length) void this.refreshWarehouseOptions(true);
    });

    let pricingClientId = Number(this.client?.id || 0);
    this.clientFormControl.valueChanges.subscribe((client) => {
      const cars: ClientCar[] = client?.cars || [];
      const selectedCar = this.clientCar;
      const selectedCarBelongsToClient =
        !!selectedCar && cars.some((car) => car.id === selectedCar.id);

      if (client?.id && !selectedCarBelongsToClient) {
        this.clientCarFormControl.setValue(cars.length === 1 ? cars[0] : null);
      }
      if (client?.id) {
        this.markupService.loadClientMarkups(client.id);
      }
      const nextClientId = Number(client?.id || 0);
      if (nextClientId !== pricingClientId) {
        pricingClientId = nextClientId;
        for (const row of this.productsFormArray.controls) this.refreshSupplierRetailPrice(row);
      }
      this.prefillNovaPoshtaRecipientFromClient();
    });

    this.applyCreatePrefillFromQueryParams();
  }

  isNovaPoshtaDelivery() {
    return this.deliveryMethodControl.value === OrderDeliveryMethod.NOVA_POSHTA;
  }

  displayNovaPoshtaCity(city?: NovaPoshtaCity | string) {
    if (!city) return '';
    if (typeof city === 'string') return city;
    return city.present || city.name || '';
  }

  displayNovaPoshtaWarehouse(warehouse?: NovaPoshtaWarehouse | string) {
    if (!warehouse) return '';
    if (typeof warehouse === 'string') return warehouse;
    return warehouse.shortAddress || warehouse.description || '';
  }

  getNovaPoshtaWarehouseKindLabel(warehouse: NovaPoshtaWarehouse) {
    const kindLabel = warehouse.kind === NovaPoshtaDeliveryKind.POSTOMAT ? 'Почтомат' : 'Отделение';
    return warehouse.number ? `${kindLabel} №${warehouse.number}` : kindLabel;
  }

  onNovaPoshtaCitySelected() {
    this.novaPoshtaWarehouseControl.setValue(null);
  }

  onNovaPoshtaWarehouseInputActive() {
    this.isNovaPoshtaWarehouseListOpen = true;
    if (this.novaPoshtaWarehouseActiveIndex < 0 && this.novaPoshtaWarehouseResults.length) {
      this.novaPoshtaWarehouseActiveIndex = 0;
    }
    this.openNovaPoshtaWarehousePanel();
  }

  onNovaPoshtaWarehouseInputBlur() {
    window.setTimeout(() => {
      this.isNovaPoshtaWarehouseListOpen = false;
    }, 150);
  }

  onNovaPoshtaWarehouseKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.isNovaPoshtaWarehouseListOpen) {
      event.preventDefault();
      this.isNovaPoshtaWarehouseListOpen = false;
      return;
    }

    const results = this.novaPoshtaWarehouseResults;
    if (!results.length) {
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.isNovaPoshtaWarehouseListOpen = true;
      this.novaPoshtaWarehouseActiveIndex =
        this.novaPoshtaWarehouseActiveIndex < 0
          ? 0
          : Math.min(this.novaPoshtaWarehouseActiveIndex + 1, results.length - 1);
      this.scrollNovaPoshtaWarehouseActiveOptionIntoView();
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.isNovaPoshtaWarehouseListOpen = true;
      this.novaPoshtaWarehouseActiveIndex =
        this.novaPoshtaWarehouseActiveIndex < 0
          ? results.length - 1
          : Math.max(this.novaPoshtaWarehouseActiveIndex - 1, 0);
      this.scrollNovaPoshtaWarehouseActiveOptionIntoView();
      return;
    }

    if (event.key === 'Enter' && this.isNovaPoshtaWarehouseListOpen) {
      const selected = results[this.novaPoshtaWarehouseActiveIndex];
      if (selected) {
        event.preventDefault();
        this.selectNovaPoshtaWarehouse(selected);
      }
    }
  }

  selectNovaPoshtaWarehouse(warehouse: NovaPoshtaWarehouse) {
    this.novaPoshtaWarehouseControl.setValue(warehouse);
    this.isNovaPoshtaWarehouseListOpen = false;
  }

  async loadBySelected() {
    if (!this.user || !this.cartSelectionAllowed || this.cartSelectionApplied || !this.passedProducts) return;
    this.cartSelectionApplied = true;
    for (const row of this.passedProducts) {
      await this.addProduct(row.product);
      const ctrl = this.findProductControl(row.product);
      if (!ctrl)
        return this.notificationService.warning(
          'Ошибка, не могу найти контроль продукта в документе!'
        );
      ctrl.patchValue({
        quan: Number(row.quan),
        price: Number(row.retail),
        sum: Math.round(Number(row.quan) * Number(row.retail) * 100) / 100
      });
      this.keepManualRetailPrice(ctrl);
      if (!this.editedSourceRows.has(ctrl)) {
        this.getWarehouseAllocations(ctrl).clear();
        this.initializedWarehouseRows.delete(ctrl);
      }
      await this.suggestDraftSupplyForRow(ctrl, true);
    }
  }

  createClientCar() {
    const dialog = this.dialog.open(CreateCarComponent);
    setTimeout(() => {
      dialog.componentInstance.clientId = this.client?.id;
      dialog.componentInstance.createCarEvent.subscribe((newCar) => {
        dialog.close();
        if (this.client) {
          if (this.client.cars) {
            this.client.cars.push(newCar);
          } else {
            this.client.cars = [newCar];
          }
        }
        this.clientCarFormControl.setValue(newCar);
      });
    });
  }

  createClientCarFromOption(isUserInput: boolean) {
    if (isUserInput) {
      this.createClientCar();
    }
  }

  private normalizeClientCarSearch(value: string | null | undefined): string {
    return String(value || '')
      .trim()
      .toLocaleLowerCase();
  }

  loadDoc() {
    this.clearAutomaticSupplySuggestions();
    for (const row of this.supplierPriceRequests.keys()) this.cancelSupplierRetailPrice(row);
    this.supplierPriceErrors.clear();
    if (!this.order) return this.notificationService.warning('Ошибка! Не могу найти документ!');

    this.productsFormArray.clear();
    this.warehouseForms.clear();
    this.warehouseSourcesChanged = false;
    this.supplyForms.clear();
    this.pendingStockReleases.clear();
    this.removedProductsCount = 0;
    this.warehousePickerRow = null;
    this.clientFormControl.setValue(this.order.client);
    this.descriptionFormControl.setValue(this.order.description);
    this.loadDelivery(this.order.delivery);
    this.dateExpectedFormControl.setValue(this.order.date_expected);
    this.timeExpectedFormControl.setValue(
      [this.order.date_expected.getHours(), this.order.date_expected.getMinutes(), this.order.date_expected.getSeconds()]
        .map((value) => String(value).padStart(2, '0')).join(':')
    );
    this.clientCarFormControl.setValue(this.order.client_car || null);
    this.warehouseControl.setValue(this.order.warehouse);
    this.order.products.forEach((prod) => {
      const savedRow = {
        id: prod.id,
        quan: +prod.quan,
        price: +prod.price,
        sum: Math.round(prod.quan * prod.price * 100) / 100,
        provider: prod.provider,
        product: prod.product
      };
      // Saved row IDs own independent reservations, suppliers and prices, even for one SKU.
      this.productsFormArray.push(this.createProductInput(savedRow));
    });
    this.updateView();
  }

  findProductControl(product: Product) {
    return this.productsFormArray.controls.find((ctrl) => ctrl.value.product.id === product.id);
  }

  clearCart() {
    if (!this.user || !this.user.cart) return;
    this.store.dispatch(clearCart());
  }

  async save(orderSuppliers = false): Promise<boolean> {
    if (this.saving || this.supplySaving || this.removeConfirmationPending) return false;
    if (this.supplierPricingPending || this.supplierPriceErrors.size) {
      this.notificationService.warning(this.supplierPricingPending
        ? 'Дождитесь пересчёта розничной цены'
        : 'Повторите расчёт розничной цены или укажите её вручную');
      return false;
    }
    if (this.automaticSupplyPending) {
      this.notificationService.warning('Дождитесь автоматического подбора обеспечения');
      return false;
    }
    this.syncOrderExpectedAtFromSupply();
    if (!this.warehouse?.id) {
      this.notificationService.warning('Выберите склад получения заказа');
      return false;
    }
    if (typeof this.client === 'string' && this.client.trim()) {
      this.notificationService.warning('Выберите клиента из списка или очистите поле');
      return false;
    }
    if (typeof this.clientCarFormControl.value === 'string' && this.clientCarFormControl.value.trim()) {
      this.notificationService.warning('Выберите или создайте автомобиль, либо очистите поле');
      return false;
    }
    if (this.id && (this.supplyLoading || !this.supply)) {
      this.notificationService.warning('Дождитесь загрузки обеспечения заказа');
      return false;
    }
    if (!this.validateSupplyPurchasePrices() || !this.validateSourceQuantities(!orderSuppliers)) return false;
    const delivery = this.buildDeliveryPayload();
    if (!delivery) return false;
    const pricingContext = this.supplierPricingContext();
    let pricingChangedDuringSave: AbstractControl[] = [];
    this.saving = true;
    try {
      const warehouseAvailable = await this.refreshWarehouseOptions();
      const currentPricingContext = this.supplierPricingContext();
      pricingChangedDuringSave = [...currentPricingContext.keys()].filter(row =>
        currentPricingContext.get(row) !== pricingContext.get(row) || this.hasSupplierPriceError(row));
      if (pricingChangedDuringSave.length || currentPricingContext.size !== pricingContext.size) {
        this.notificationService.warning('Данные для расчёта цены изменились. Проверьте новую цену и сохраните заказ ещё раз');
        return false;
      }
      if (!warehouseAvailable) return false;
      if (!this.validateSourceQuantities(!orderSuppliers)) return false;
      const reroutingWarehouse = this.warehouseChangePending;
      const products = this.productsFormArray.controls.map((control) => {
        const row = control.value as ProductInDoc;
        const allocations = this.serializeSupplyAllocations(control);
        return {
          id: row.id,
          product_id: row.product.id,
          quan: row.quan,
          price: row.price,
          // Committed suppliers are absent from the editable PLANNED allocations.
          provider_id: allocations[0]?.provider_id ??
            (reroutingWarehouse && row.id && !this.editedSourceRows.has(control)
              ? row.provider?.id : undefined),
          supply_allocations: allocations,
          warehouse_allocations: this.serializeWarehouseAllocations(control),
          warehouse_releases: this.serializeStockReleases(control)
        };
      });
      const dateExpected = new Date(this.dateExpected);
      const [hours, minutes, seconds = 0] = String(this.timeExpected).split(':').map(Number);
      if (dateExpected.getHours() !== hours || dateExpected.getMinutes() !== minutes || dateExpected.getSeconds() !== seconds) {
        dateExpected.setHours(hours, minutes, seconds, 0);
      }
      const expectedWarehouseId = Number(this.order?.warehouse?.id);
      const payload = {
        ...(this.id ? { id: this.id } : {}),
        ...(reroutingWarehouse ? { expected_warehouse_id: expectedWarehouseId } : {}),
        client_id: this.client?.id || null,
        products,
        description: this.description,
        client_car_id: this.clientCar?.id || null,
        warehouse_id: this.warehouse.id,
        delivery,
        dateExpected: dateExpected.toISOString()
      };
      const conflicts = findWarehouseStockConflicts(products, this.warehouseOptions);
      if (conflicts.length && !this.saveRequestIdentity.canRetryUncertainPayload(payload)) {
        this.showWarehouseConflicts(conflicts);
        return false;
      }
      const request_id = this.saveRequestIdentity.forPayload(payload, () => this.uuid());
      this.saveRequestIdentity.markAttemptSent(payload);
      const request = this.id
        ? reroutingWarehouse
          ? this.orderService.changeWarehouse({ ...payload, id: this.id, request_id,
              expected_warehouse_id: expectedWarehouseId })
          : this.orderService.save({ ...payload, id: this.id, request_id })
        : this.orderService.create({ ...payload, request_id });
      const result = await request.toPromise();
      if (!result?.id) throw new Error('Пустой ответ при сохранении заказа');
      this.saveRequestIdentity.acknowledgeResult();
      if (this.passedProducts) {
        this.productsInDoc.forEach((row: ProductWithControls) => {
          this.store.dispatch(removeFromCart({ payload: row.product }));
        });
      }
      if (orderSuppliers) {
        const orderId = Number(this.id || result?.id);
        let confirmed = false;
        try {
          const supply = await this.orderService.getSupply(orderId).toPromise();
          if (!supply) throw new Error('Не удалось загрузить сохранённые заказы поставщикам');
          confirmed = await this.confirmPlannedSupplierOrders(orderId, supply);
        } catch (error) {
          this.notificationService.showError(
            extractApiErrorMessage(error, 'Не удалось подтвердить заказ поставщикам')
          );
        }
        if (!confirmed) {
          // The order is already saved: reopen it with persisted row IDs before any retry.
          this.id = orderId;
          this.order = { ...result, date_expected: new Date(result.date_expected) };
          this.loadDoc();
          await this.loadSupply();
          await this.router.navigateByUrl(`/orders/update/${orderId}`);
          this.notificationService.warning(
            `Заказ №${orderId} сохранён. Заказы поставщикам не подтверждены.`
          );
          return false;
        }
      }
      this.router.navigateByUrl('/orders/list');
      this.notificationService.notice(`Заказ №${this.id || result?.id} сохранен!`);
      return true;
    } catch (error) {
      const status = Number((error as any)?.status || 0);
      if (status >= 400 && status < 500) this.saveRequestIdentity.acknowledgeResult();
      const body = (error as any)?.error;
      if (body?.code === 'WAREHOUSE_STOCK_CONFLICT') {
        await this.refreshWarehouseOptions();
        this.showWarehouseConflicts(body.conflicts || []);
      } else {
        this.handleSaveError(error);
      }
      return false;
    } finally {
      this.saving = false;
      for (const row of pricingChangedDuringSave) this.refreshSupplierRetailPrice(row);
    }
  }

  openUnavailableProductsDialog() {
    if (!this.id) return;
    const dialogRef = this.dialog.open(UnavailableProductsDialogComponent, {
      width: '760px',
      data: {
        orderId: Number(this.id)
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (!result || !this.id) return;
      this.reloadOrder();
    });
  }

  async openOrderRefusal() {
    if (!this.order?.id || this.saving || this.supplySaving) return;
    const changed = await firstValueFrom(this.dialog.open(OrderRefusalDialogComponent, {
      width: '680px', maxWidth: '96vw', data: { orderId: this.order.id }
    }).afterClosed());
    if (changed) this.reloadOrder();
  }

  private reloadOrder() {
    if (!this.id) return;
    this.orderService.getOne(this.id).subscribe((order) => {
      this.order = order;
      this.loadDoc();
      this.lastFiscalEditConflict = false;
    });
  }

  private handleSaveError(err: any) {
    const message = extractApiErrorMessage(err, 'Не удалось сохранить заказ');
    this.notificationService.warning(message);
    this.lastFiscalEditConflict = this.isFiscalInvoiceEditConflict(message);
    if (this.lastFiscalEditConflict) {
      this.openUnavailableProductsDialog();
    }
  }

  private isFiscalInvoiceEditConflict(message: string) {
    return (
      message.includes('содержит товар') ||
      message.includes('по счетам заказа превышает') ||
      message.includes('не совпадает с заказом')
    );
  }

  private setupNovaPoshtaLookups() {
    this.deliveryMethodControl.valueChanges.subscribe((method) => {
      if (method === OrderDeliveryMethod.NOVA_POSHTA) {
        this.prefillNovaPoshtaRecipientFromClient();
      }
    });

    this.novaPoshtaCityControl.valueChanges.subscribe((value) => {
      if (!value || typeof value === 'string') {
        this.novaPoshtaWarehouseControl.setValue(null, { emitEvent: false });
      }
    });

    this.filteredNovaPoshtaCities$ = this.novaPoshtaCityControl.valueChanges.pipe(
      startWith(this.novaPoshtaCityControl.value || ''),
      debounceTime(250),
      switchMap((value) => {
        const query = typeof value === 'string' ? value.trim() : '';
        if (!this.isNovaPoshtaDelivery() || !query || query.length < 2) {
          return of(value && typeof value === 'object' ? [value as NovaPoshtaCity] : []);
        }

        return this.novaPoshtaService.searchCities(query).pipe(catchError(() => of([])));
      })
    );

    this.filteredNovaPoshtaWarehouses$ = combineLatest([
      this.novaPoshtaWarehouseControl.valueChanges.pipe(
        startWith(this.novaPoshtaWarehouseControl.value || '')
      ),
      this.novaPoshtaCityControl.valueChanges.pipe(startWith(this.novaPoshtaCityControl.value)),
      this.deliveryMethodControl.valueChanges.pipe(startWith(this.deliveryMethodControl.value))
    ]).pipe(
      debounceTime(250),
      switchMap(([warehouseValue, cityValue, deliveryMethod]) => {
        if (deliveryMethod !== OrderDeliveryMethod.NOVA_POSHTA) {
          return of([]);
        }
        const city =
          cityValue && typeof cityValue === 'object' ? (cityValue as NovaPoshtaCity) : null;
        if (!city?.ref) {
          return of([]);
        }
        if (warehouseValue && typeof warehouseValue === 'object') {
          return of([warehouseValue as NovaPoshtaWarehouse]);
        }

        const query = String(warehouseValue || '').trim();
        return merge(
          this.novaPoshtaService
            .searchWarehouses({
              cityRef: city.ref,
              kind: NovaPoshtaDeliveryKind.WAREHOUSE,
              query
            })
            .pipe(catchError(() => of([]))),
          this.novaPoshtaService
            .searchWarehouses({
              cityRef: city.ref,
              kind: NovaPoshtaDeliveryKind.POSTOMAT,
              query
            })
            .pipe(catchError(() => of([])))
        ).pipe(
          scan(
            (warehouses, group) => this.mergeNovaPoshtaWarehouses([warehouses, group], query),
            [] as NovaPoshtaWarehouse[]
          )
        );
      }),
      tap((warehouses) => {
        this.novaPoshtaWarehouseResults = warehouses;
        this.novaPoshtaWarehouseActiveIndex = warehouses.length ? 0 : -1;
        if (warehouses.length) {
          this.openNovaPoshtaWarehousePanel();
        }
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.novaPoshtaWarehouseLookupSubscription = this.filteredNovaPoshtaWarehouses$.subscribe();
  }

  private mergeNovaPoshtaWarehouses(groups: NovaPoshtaWarehouse[][], query = '') {
    const seen = new Set<string>();
    return groups
      .reduce((warehouses, group) => warehouses.concat(group), [] as NovaPoshtaWarehouse[])
      .filter((warehouse) => {
        if (!warehouse?.warehouseRef || seen.has(warehouse.warehouseRef)) {
          return false;
        }
        seen.add(warehouse.warehouseRef);
        return true;
      })
      .map((warehouse, index) => ({ warehouse, index }))
      .sort((a, b) => {
        const scoreDiff =
          this.getNovaPoshtaWarehouseSearchScore(a.warehouse, query) -
          this.getNovaPoshtaWarehouseSearchScore(b.warehouse, query);
        if (scoreDiff !== 0) {
          return scoreDiff;
        }

        const kindDiff =
          this.getNovaPoshtaWarehouseKindRank(a.warehouse) -
          this.getNovaPoshtaWarehouseKindRank(b.warehouse);
        return kindDiff || a.index - b.index;
      })
      .map(({ warehouse }) => warehouse)
      .slice(0, 25);
  }

  private scrollNovaPoshtaWarehouseActiveOptionIntoView() {
    window.setTimeout(() => {
      const option = this.novaPoshtaWarehouseOptions?.get(this.novaPoshtaWarehouseActiveIndex);
      option?.nativeElement.scrollIntoView({ block: 'nearest' });
    });
  }

  private getNovaPoshtaWarehouseSearchScore(warehouse: NovaPoshtaWarehouse, query: string) {
    const normalizedQuery = this.normalizeNovaPoshtaSearchText(query);
    const queryDigits = normalizedQuery.replace(/\D/g, '');
    const numberDigits = this.normalizeNovaPoshtaSearchText(warehouse.number).replace(/\D/g, '');
    const description = this.normalizeNovaPoshtaSearchText(
      `${warehouse.description || ''} ${warehouse.shortAddress || ''}`
    );

    if (queryDigits && numberDigits === queryDigits) {
      return 0;
    }
    if (queryDigits && numberDigits.startsWith(queryDigits)) {
      return 1;
    }
    if (queryDigits && numberDigits.includes(queryDigits)) {
      return 2;
    }
    if (normalizedQuery && description.includes(normalizedQuery)) {
      return 3;
    }

    return 4;
  }

  private getNovaPoshtaWarehouseKindRank(warehouse: NovaPoshtaWarehouse) {
    return warehouse.kind === NovaPoshtaDeliveryKind.WAREHOUSE ? 0 : 1;
  }

  private normalizeNovaPoshtaSearchText(value: unknown) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  private openNovaPoshtaWarehousePanel() {
    window.setTimeout(() => {
      const input = this.novaPoshtaWarehouseInput?.nativeElement;
      if (
        input &&
        document.activeElement === input &&
        this.isNovaPoshtaDelivery() &&
        this.selectedNovaPoshtaCity
      ) {
        this.novaPoshtaWarehouseTrigger?.openPanel();
      }
    });
  }

  private buildDeliveryPayload(): OrderDelivery | null {
    if (!this.isNovaPoshtaDelivery()) {
      return {
        method: OrderDeliveryMethod.PICKUP
      };
    }

    const city = this.selectedNovaPoshtaCity;
    const warehouse = this.novaPoshtaWarehouseControl.value as NovaPoshtaWarehouse | string | null;
    const recipientFirstName = this.normalizeNovaPoshtaRecipientText(
      this.novaPoshtaRecipientFirstNameControl.value
    );
    const recipientLastName = this.normalizeNovaPoshtaRecipientText(
      this.novaPoshtaRecipientLastNameControl.value
    );
    const recipientPhone = this.normalizeNovaPoshtaRecipientPhone(
      this.novaPoshtaRecipientPhoneControl.value
    );
    if (!city?.ref || !warehouse || typeof warehouse === 'string' || !warehouse.warehouseRef) {
      this.notificationService.warning('Выберите город и отделение или почтомат Новой почты');
      return null;
    }
    if (!recipientFirstName || !recipientLastName) {
      this.notificationService.warning('Укажите имя и фамилию получателя Новой почты');
      return null;
    }
    if (recipientPhone.replace(/\D/g, '').length < 9) {
      this.notificationService.warning('Укажите телефон получателя Новой почты');
      return null;
    }
    this.novaPoshtaKindControl.setValue(warehouse.kind || NovaPoshtaDeliveryKind.WAREHOUSE, {
      emitEvent: false
    });

    return {
      method: OrderDeliveryMethod.NOVA_POSHTA,
      novaPoshta: {
        kind: warehouse.kind || NovaPoshtaDeliveryKind.WAREHOUSE,
        recipientFirstName,
        recipientLastName,
        recipientPhone,
        cityRef: city.ref,
        cityName: city.name || city.present,
        settlementRef: city.settlementRef,
        areaName: city.area,
        regionName: city.region,
        warehouseRef: warehouse.warehouseRef,
        warehouseDescription: warehouse.description,
        warehouseNumber: warehouse.number,
        warehouseTypeRef: warehouse.warehouseTypeRef,
        warehouseShortAddress: warehouse.shortAddress
      }
    };
  }

  private loadDelivery(delivery?: OrderDelivery) {
    if (!delivery || delivery.method !== OrderDeliveryMethod.NOVA_POSHTA || !delivery.novaPoshta) {
      this.deliveryMethodControl.setValue(OrderDeliveryMethod.PICKUP);
      this.novaPoshtaCityControl.setValue(null);
      this.novaPoshtaWarehouseControl.setValue(null);
      this.novaPoshtaRecipientFirstNameControl.setValue(null);
      this.novaPoshtaRecipientLastNameControl.setValue(null);
      this.novaPoshtaRecipientPhoneControl.setValue(null);
      return;
    }

    const novaPoshta = delivery.novaPoshta;
    this.deliveryMethodControl.setValue(OrderDeliveryMethod.NOVA_POSHTA);
    this.novaPoshtaKindControl.setValue(novaPoshta.kind || NovaPoshtaDeliveryKind.WAREHOUSE);
    this.novaPoshtaRecipientFirstNameControl.setValue(
      novaPoshta.recipientFirstName || this.order?.client?.name || null
    );
    this.novaPoshtaRecipientLastNameControl.setValue(
      novaPoshta.recipientLastName || this.order?.client?.surname || null
    );
    this.novaPoshtaRecipientPhoneControl.setValue(
      novaPoshta.recipientPhone || getClientPrimaryPhone(this.order?.client) || null
    );
    this.novaPoshtaCityControl.setValue({
      ref: novaPoshta.cityRef,
      name: novaPoshta.cityName,
      present: [novaPoshta.cityName, novaPoshta.regionName, novaPoshta.areaName]
        .filter(Boolean)
        .join(', '),
      settlementRef: novaPoshta.settlementRef,
      area: novaPoshta.areaName,
      region: novaPoshta.regionName
    });
    this.novaPoshtaWarehouseControl.setValue({
      warehouseRef: novaPoshta.warehouseRef,
      number: novaPoshta.warehouseNumber,
      description: novaPoshta.warehouseDescription,
      shortAddress: novaPoshta.warehouseShortAddress,
      kind: novaPoshta.kind,
      cityRef: novaPoshta.cityRef,
      warehouseTypeRef: novaPoshta.warehouseTypeRef
    });
  }

  private prefillNovaPoshtaRecipientFromClient() {
    if (!this.isNovaPoshtaDelivery() || !this.client) {
      return;
    }

    if (!this.normalizeNovaPoshtaRecipientText(this.novaPoshtaRecipientFirstNameControl.value)) {
      this.novaPoshtaRecipientFirstNameControl.setValue(this.client.name || null, {
        emitEvent: false
      });
    }
    if (!this.normalizeNovaPoshtaRecipientText(this.novaPoshtaRecipientLastNameControl.value)) {
      this.novaPoshtaRecipientLastNameControl.setValue(this.client.surname || null, {
        emitEvent: false
      });
    }
    if (!this.normalizeNovaPoshtaRecipientPhone(this.novaPoshtaRecipientPhoneControl.value)) {
      this.novaPoshtaRecipientPhoneControl.setValue(getClientPrimaryPhone(this.client), {
        emitEvent: false
      });
    }
  }

  private normalizeNovaPoshtaRecipientText(value: unknown) {
    return String(value || '')
      .trim()
      .replace(/\s+/g, ' ');
  }

  private normalizeNovaPoshtaRecipientPhone(value: unknown) {
    return String(value || '')
      .trim()
      .replace(/[^0-9+]/g, '');
  }

  private applyCreatePrefillFromQueryParams() {
    if (this.id || this.order) {
      return;
    }

    const params = this.activateRoute.snapshot?.queryParamMap;
    const clientId = this.getNumericQueryParam(params, 'client_id', 'clientId');
    const clientCarId = this.getNumericQueryParam(params, 'client_car_id', 'clientCarId');
    if (!clientId) {
      return;
    }

    this.clientService.getOne(clientId).subscribe(
      (client) => {
        if (!client) {
          return;
        }

        this.clientFormControl.setValue(client);
        const selectedCar = this.getPrefillClientCar(client, clientCarId);
        if (selectedCar) {
          this.clientCarFormControl.setValue(selectedCar);
        }
      },
      (err) => {
        this.notificationService.warning(err?.error?.message || 'Не удалось заполнить клиента');
      }
    );
  }

  private getPrefillClientCar(client: Client, clientCarId: number): ClientCar | null {
    const cars = client.cars || [];
    if (!cars.length) {
      return null;
    }

    const selectedCar = clientCarId
      ? cars.find((car) => Number(car.id) === clientCarId) || null
      : null;
    if (selectedCar) {
      return selectedCar;
    }

    return !clientCarId && cars.length === 1 ? cars[0] : null;
  }

  private getNumericQueryParam(
    params: { get: (name: string) => string | null } | null | undefined,
    ...keys: string[]
  ) {
    for (const key of keys) {
      const value = Number(params?.get(key) || 0);
      if (Number.isFinite(value) && value > 0) {
        return value;
      }
    }

    return 0;
  }

  updateView() {
    this.dataSource.next(this.products);
  }

  dropProductRow(event: CdkDragDrop<AbstractControl[]>) {
    if (this.saving || this.supplySaving || !event.isPointerOverContainer) return;
    const previousIndex = this.products.indexOf(event.item.data);
    const currentIndex = event.currentIndex;
    if (previousIndex < 0 || !Number.isInteger(currentIndex) || currentIndex < 0 ||
      currentIndex >= this.products.length || previousIndex === currentIndex) return;

    const activeRow = this.products[this.activeProductIndex];
    // Move the existing control so row IDs, source forms and disclosure state stay together.
    const row = this.productsFormArray.at(previousIndex);
    this.productsFormArray.removeAt(previousIndex, { emitEvent: false });
    this.productsFormArray.insert(currentIndex, row, { emitEvent: false });
    this.productsFormArray.updateValueAndValidity();
    this.productsFormArray.markAsDirty();
    this.activeProductIndex = this.products.indexOf(activeRow);
    this.warehousePickerRow = null;
    this.hideProductImagePreview();
    this.updateView();
  }

  createProductInput(prod: ProductInDoc): UntypedFormGroup {
    return this.fb.group(prod);
  }

  async loadSupply() {
    if (!this.id) return;
    this.supplyLoading = true;
    try {
      const supply = await this.orderService.getSupply(this.id).toPromise();
      if (!supply) throw new Error('Пустой ответ сервера');
      this.supply = supply;
      this.supplyForms.clear();
      for (const line of supply.lines) {
        const productControl = this.productsFormArray.controls.find(
          (control) => Number(control.value.id) === Number(line.order_product_id)
        );
        if (!productControl) continue;
        const rows = line.allocations
          .filter((allocation) => allocation.status === 'PLANNED')
          .map((allocation) =>
            this.createSupplyAllocationForm(
              productControl,
              {
                id: allocation.provider_id,
                description: allocation.provider
              },
              allocation.quantity,
              allocation.purchase_price,
              false,
              allocation.contract_id
                ? this.buildSupplyContract(allocation.contract_id, allocation.contract_number, {
                    id: allocation.provider_id,
                    description: allocation.provider
                  })
                : null,
              allocation.expected_at,
              allocation.provider_warehouse_id,
              allocation.expected_at_confirmed === false
            )
          );
        this.supplyForms.set(productControl, this.fb.array(rows));
      }
      this.syncOrderExpectedAtFromSupply();
      await this.refreshWarehouseOptions(true);
    } catch (error) {
      console.error(error);
      this.notificationService.showError('Не удалось загрузить обеспечение заказа');
    } finally {
      this.supplyLoading = false;
    }
  }

  getSupplyLine(row: AbstractControl): OrderSupplyLine | undefined {
    return this.supply?.lines.find(
      (line) => Number(line.order_product_id) === Number(row.value.id)
    );
  }

  getEditableSupplyLine(row: AbstractControl): OrderSupplyLine | undefined {
    const saved = this.getSupplyLine(row);
    if (saved) {
      const release = this.getPendingStockRelease(row);
      if (!release) return saved;
      return { ...saved, reserved: this.roundSupplyQuantity(Math.max(saved.reserved - release.quantity, 0)),
        picked: 0,
        placed_quantity: Math.max(Number(saved.placed_quantity || 0) - release.picked, 0),
        ready_quantity: Math.max(Number(saved.ready_quantity || 0) - release.picked, 0),
        uncovered: this.roundSupplyQuantity(saved.uncovered + release.quantity) };
    }
    if (this.id && row.value.id) return undefined;

    const required = this.roundSupplyQuantity(row.value.quan || 0);
    const reserved = 0;
    return {
      order_product_id: 0,
      product_id: Number(row.value.product?.id || 0),
      required,
      reserved,
      planned: 0,
      ordered: 0,
      received: 0,
      transfer_reserved: 0,
      transfer_picking: 0,
      transfer_in_transit: 0,
      moving_doc_ids: [],
      uncovered: this.roundSupplyQuantity(Math.max(required - reserved, 0)),
      allocations: []
    };
  }

  getPendingStockRelease(row: AbstractControl) {
    return this.pendingStockReleases?.get(row);
  }

  declineStockReservation(row: AbstractControl) {
    if (this.saving || this.supplySaving || this.supplyLoading || !row.value.id) return;
    const line = this.getSupplyLine(row);
    if (!line || line.reserved <= 0 || this.getPendingStockRelease(row)) return;
    const warehouseId = Number(line.warehouse_id || this.warehouse?.id);
    if (!warehouseId) return;
    this.pendingStockReleases.set(row, {
      warehouse_id: warehouseId, quantity: Number(line.reserved),
      warehouse_name: line.warehouse_name || this.warehouse?.description || 'Склад заказа',
      picked: Number(line.picked || 0)
    });
    this.markSourcesEdited(row);
    this.expandedSupplyRows.add(row);
    this.warehousePickerRow = null;
    this.syncOrderExpectedAtFromSupply();
  }

  undoStockReservationRelease(row: AbstractControl) {
    if (this.saving || this.supplySaving || !this.getPendingStockRelease(row)) return;
    this.pendingStockReleases.delete(row);
    this.markSourcesEdited(row);
    this.syncOrderExpectedAtFromSupply();
  }

  private serializeStockReleases(row: AbstractControl) {
    const release = this.getPendingStockRelease(row);
    return release ? [{ warehouse_id: release.warehouse_id, quantity: release.quantity }] : [];
  }

  getWarehouseAllocations(row: AbstractControl): UntypedFormArray {
    let form = this.warehouseForms.get(row);
    if (!form) {
      form = this.fb.array([]);
      this.warehouseForms.set(row, form);
    }
    return form;
  }

  private markSourcesEdited(row: AbstractControl) {
    this.editedSourceRows.add(row);
    this.bumpDraftSupplySuggestionVersion(row);
    this.cancelAutomaticSupply(row);
    this.createForm.markAsDirty();
  }

  private createWarehouseAllocation(
    row: AbstractControl,
    source: {
      warehouse_id: number;
      warehouse_name: string;
      quantity: number;
    },
    autoSuggested = false
  ) {
    const group = this.fb.group({
      autoSuggested,
      warehouse_id: source.warehouse_id,
      warehouse_name: source.warehouse_name,
      quantity: new UntypedFormControl(source.quantity, [Validators.required, Validators.min(0.01)])
    });
    group.valueChanges.subscribe(() => {
      group.get('autoSuggested')?.setValue(false, { emitEvent: false });
      this.warehouseSourcesChanged = true;
      this.markSourcesEdited(row);
    });
    return group;
  }

  get hasWarehouseDrafts() {
    return this.productsFormArray.controls.some(
      (row) => this.getWarehouseAllocations(row).length > 0
    );
  }

  private serializeWarehouseAllocations(row: AbstractControl) {
    return this.getWarehouseAllocations(row).controls.map((control) => ({
      warehouse_id: Number(control.value.warehouse_id),
      quantity: Number(control.value.quantity)
    }));
  }

  getWarehouseOptions(row: AbstractControl) {
    return this.warehouseOptions.filter(
      (option) => option.product_id === Number(row.value.product?.id)
    );
  }

  getWarehouseOption(row: AbstractControl, allocation: AbstractControl) {
    return this.getWarehouseOptions(row).find(
      (option) => option.warehouse_id === Number(allocation.value.warehouse_id)
    );
  }

  getWarehouseRemaining(option: PartsSelectionWarehouseOption) {
    const assigned = this.productsFormArray.controls
      .filter((row) => Number(row.value.product?.id) === option.product_id)
      .reduce<Array<{ warehouse_id: number; quantity: number }>>(
        (all, row) => all.concat(this.serializeWarehouseAllocations(row)),
        []
      )
      .filter((allocation) => allocation.warehouse_id === option.warehouse_id)
      .reduce((sum, allocation) => sum + Math.max(allocation.quantity, 0), 0);
    return this.roundSupplyQuantity(Math.max(Number(option.available) - assigned, 0));
  }

  async toggleWarehousePicker(row: AbstractControl) {
    if (this.saving) return;
    this.warehousePickerRow = this.warehousePickerRow === row ? null : row;
    if (this.warehousePickerRow) await this.refreshWarehouseOptions(true);
  }

  addWarehouseAllocation(row: AbstractControl, option: PartsSelectionWarehouseOption) {
    if (this.saving || this.warehouseOptionsLoading || this.warehouseOptionsError) return;
    if (this.getPendingStockRelease(row)?.warehouse_id === option.warehouse_id) {
      this.notificationService.warning('Сначала отмените отказ от резерва этого склада');
      return;
    }
    const line = this.getEditableSupplyLine(row);
    const quantity = Math.min(
      line ? this.getSupplyCoverage(row, line).remaining : 0,
      this.getWarehouseRemaining(option)
    );
    if (quantity <= 0) return;
    this.markSourcesEdited(row);
    this.warehouseSourcesChanged = true;
    const existing = this.getWarehouseAllocations(row).controls.find(
      (control) => Number(control.value.warehouse_id) === option.warehouse_id
    );
    if (existing) {
      existing
        .get('quantity')
        ?.setValue(this.roundSupplyQuantity(Number(existing.value.quantity) + quantity));
    } else {
      this.getWarehouseAllocations(row).push(
        this.createWarehouseAllocation(row, { ...option, quantity })
      );
    }
  }

  removeWarehouseAllocation(row: AbstractControl, index: number) {
    if (this.saving) return;
    this.markSourcesEdited(row);
    this.getWarehouseAllocations(row).removeAt(index);
    this.warehouseSourcesChanged = true;
  }

  isWarehouseConflict(row: AbstractControl, allocation: AbstractControl) {
    return this.warehouseConflicts.some(
      (conflict) =>
        conflict.product_id === Number(row.value.product?.id) &&
        conflict.warehouse_id === Number(allocation.value.warehouse_id)
    );
  }

  async refreshWarehouseOptions(allowDefaults = false): Promise<boolean> {
    const warehouseId = Number(this.warehouse?.id || this.user?.defaultWarehouse?.id || 0);
    const productIds = [
      ...new Set(
        this.productsFormArray.controls
          .map((row) => Number(row.value.product?.id))
          .filter((id) => id > 0)
      )
    ];
    if (!productIds.length) return true;
    if (!warehouseId) return false;
    const version = ++this.warehouseOptionsVersion;
    this.warehouseOptionsLoading = true;
    this.warehouseOptionsError = '';
    try {
      const options: PartsSelectionWarehouseOption[] = [];
      let checkedAt = '';
      for (let offset = 0; offset < productIds.length; offset += 200) {
        const response = await this.partsSelectionService
          .resolveOfferWarehouseOptions(productIds.slice(offset, offset + 200), warehouseId)
          .toPromise();
        if (!response) throw new Error('Пустой ответ сервера');
        options.push(...response.items);
        checkedAt = response.checked_at;
      }
      if (
        version !== this.warehouseOptionsVersion ||
        warehouseId !== Number(this.warehouse?.id || this.user?.defaultWarehouse?.id)
      )
        return false;
      this.warehouseOptions = options.sort(
        (a, b) =>
          Number(b.is_target) - Number(a.is_target) ||
          Number(b.has_route) - Number(a.has_route) ||
          Number(b.available) - Number(a.available) ||
          a.warehouse_name.localeCompare(b.warehouse_name)
      );
      this.warehouseCheckedAt = checkedAt;
      this.warehouseConflicts = [];
      if (allowDefaults) this.initializeWarehouseSources();
      return true;
    } catch (error) {
      if (version === this.warehouseOptionsVersion) {
        this.warehouseOptionsError =
          'Не удалось обновить остатки. Повторите проверку перед сохранением.';
        if (this.saving) this.notificationService.warning(this.warehouseOptionsError);
      }
      return false;
    } finally {
      if (version === this.warehouseOptionsVersion) this.warehouseOptionsLoading = false;
    }
  }

  private initializeWarehouseSources() {
    const assignedByProduct = new Map<number, Map<number, number>>();
    for (const row of this.productsFormArray.controls) {
      const productId = Number(row.value.product?.id);
      const assigned = assignedByProduct.get(productId) || new Map<number, number>();
      for (const allocation of this.serializeWarehouseAllocations(row)) {
        assigned.set(
          allocation.warehouse_id,
          (assigned.get(allocation.warehouse_id) || 0) + Math.max(allocation.quantity, 0)
        );
      }
      assignedByProduct.set(productId, assigned);
    }
    for (const row of this.productsFormArray.controls) {
      if (this.initializedWarehouseRows.has(row) || this.editedSourceRows.has(row)) continue;
      const line = this.getEditableSupplyLine(row);
      if (!line) continue;
      if (this.getSupplyAllocations(row).length || this.getWarehouseAllocations(row).length)
        continue;
      const productId = Number(row.value.product?.id);
      const assigned = assignedByProduct.get(productId)!;
      const allocations = allocateAvailableWarehouseStock(
        this.getSupplyCoverage(row, line).remaining,
        this.getWarehouseOptions(row).filter(option => !line.excluded_warehouse_ids?.includes(option.warehouse_id)),
        assigned
      );
      for (const allocation of allocations) {
        this.getWarehouseAllocations(row).push(this.createWarehouseAllocation(row, allocation, true));
        assigned.set(
          allocation.warehouse_id,
          (assigned.get(allocation.warehouse_id) || 0) + allocation.quantity
        );
      }
      if (allocations.length) {
        this.initializedWarehouseRows.add(row);
        this.warehouseSourcesChanged = true;
        this.createForm.markAsDirty();
      }
    }
  }

  canPreserveUnreconciledSupply(row: AbstractControl): boolean {
    const line = this.getSupplyLine(row);
    if (!this.id || Number(this.supply?.order_id) !== Number(this.id) || !line ||
      !Number(row.value.id) || Number(row.value.product?.id) !== Number(line.product_id) ||
      !Number(line.warehouse_id) || Number(this.warehouse?.id) !== Number(line.warehouse_id) ||
      this.warehouseChangePending || this.getPendingStockRelease(row) ||
      this.getWarehouseAllocations(row).length || !this.hasUnchangedSupplierPlan(row, line)) return false;
    const required = Number(row.value.quan);
    if (!Number.isFinite(required) || Math.abs(required - Number(line.required)) > 0.0001) return false;
    const coverage = this.getSupplyCoverage(row, line);
    const unattributed = Number(line.unattributed_reserved_quantity || 0);
    const local = Number(line.reserved || 0) + Number(line.fulfilled || 0);
    const orderedOpen = line.allocations
      .filter((allocation) => ['ORDERED', 'PARTIALLY_RECEIVED'].includes(allocation.status))
      .reduce((sum, allocation) => sum + orderSupplierReceipt(allocation).open, 0);
    // Preserve an existing ambiguity without assigning an unconfirmed receipt to a supplier.
    // The server rechecks the unchanged line and receipt/reservation provenance under its lock.
    return coverage.status === 'over' && Number.isFinite(unattributed) &&
      coverage.difference <= Math.min(unattributed, local, orderedOpen) + 0.0001;
  }

  private hasUnchangedSupplierPlan(row: AbstractControl, line: OrderSupplyLine): boolean {
    const submitted = this.serializeSupplyAllocations(row);
    const saved = line.allocations.filter((allocation) => allocation.status === 'PLANNED');
    if (submitted.length !== this.getSupplyAllocations(row).length || submitted.length !== saved.length) return false;
    const canonical = (source: {
      provider_id: number; contract_id?: number | null; provider_warehouse_id?: number | null;
      quantity: number; purchase_price?: number | null; expected_at?: string | null;
    }) => {
      const expected = source.expected_at ? new Date(source.expected_at).getTime() : null;
      return JSON.stringify([
        Number(source.provider_id), Number(source.contract_id || 0), Number(source.provider_warehouse_id || 0),
        Number(source.quantity), source.purchase_price == null ? null : Number(source.purchase_price),
        expected === null || Number.isFinite(expected) ? expected : source.expected_at
      ]);
    };
    return JSON.stringify(submitted.map(canonical).sort()) === JSON.stringify(saved.map(canonical).sort());
  }

  private validateSourceQuantities(allowUnchangedUnreconciled = false) {
    for (const row of this.productsFormArray.controls) {
      const line = this.getEditableSupplyLine(row);
      if (!line) continue;
      const invalid = [
        ...this.getWarehouseAllocations(row).controls,
        ...this.getSupplyAllocations(row).controls
      ].some(
        (allocation) =>
          !Number.isFinite(Number(allocation.value.quantity)) ||
          Number(allocation.value.quantity) <= 0
      );
      if (!invalid && this.getSupplyCoverage(row, line).status !== 'over') continue;
      if (!invalid && allowUnchangedUnreconciled && this.canPreserveUnreconciledSupply(row)) continue;
      this.expandedSupplyRows.add(row);
      this.notificationService.warning(
        invalid
          ? 'Укажите количество больше нуля для каждого источника'
          : 'Распределено больше, чем заказано. Уменьшите количество в источниках'
      );
      this.focusSupplyRow(Number(row.value.product?.id));
      return false;
    }
    return true;
  }

  private showWarehouseConflicts(conflicts: WarehouseStockConflict[]) {
    this.warehouseConflicts = conflicts;
    for (const conflict of conflicts) {
      const option = this.warehouseOptions.find(
        (item) =>
          item.product_id === conflict.product_id && item.warehouse_id === conflict.warehouse_id
      );
      if (option) option.available = Number(conflict.available);
    }
    this.notificationService.warning(
      'Доступный остаток изменился. Проверьте выделенные складские источники; выбранные количества сохранены'
    );
    const first = conflicts[0];
    if (first) this.focusSupplyRow(first.product_id, first.warehouse_id);
  }

  private focusSupplyRow(productId: number, warehouseId?: number) {
    for (const row of this.productsFormArray.controls) {
      if (Number(row.value.product?.id) === productId) this.expandedSupplyRows.add(row);
    }
    if (typeof document === 'undefined') return;
    const selector =
      `[data-supply-product-id="${Number(productId)}"]` +
      (warehouseId ? ` [data-supply-warehouse-id="${Number(warehouseId)}"]` : '');
    setTimeout(() => {
      const element = document.querySelector<HTMLElement>(selector);
      element?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      element?.querySelector<HTMLInputElement>('input')?.focus();
    }, 0);
  }

  getSupplyAllocations(row: AbstractControl): UntypedFormArray {
    let form = this.supplyForms.get(row);
    if (!form) {
      form = this.fb.array([]);
      this.supplyForms.set(row, form);
    }
    return form;
  }

  getSupplyProviderControl(control: AbstractControl) {
    return control.get('provider') as UntypedFormControl;
  }

  getSupplyContractControl(control: AbstractControl) {
    return control.get('contract') as UntypedFormControl;
  }

  getSupplyProviderOptions(row: AbstractControl) {
    const contextKey = this.getSupplyProviderOptionsContextKey(row);
    return contextKey ? this.supplyProviderOptionsByContext.get(contextKey) || [] : [];
  }

  isSupplyProviderOptionsLoading(row: AbstractControl) {
    const contextKey = this.getSupplyProviderOptionsContextKey(row);
    return Boolean(contextKey && this.loadingSupplyProviderOptionContexts.has(contextKey));
  }

  loadSupplyProviderOptions(row: AbstractControl) {
    void this.ensureSupplyProviderOptions(row);
  }

  private ensureSupplyProviderOptions(
    row: AbstractControl, automaticQuantity?: number
  ): Promise<PartsSelectionOfferSupplierOption[]> {
    const productId = Number(row.value.product?.id || 0);
    const contextKey = this.getSupplyProviderOptionsContextKey(row, automaticQuantity);
    if (!productId || !contextKey) return Promise.resolve([]);

    const cached = this.supplyProviderOptionsByContext.get(contextKey);
    if (cached) return Promise.resolve(cached);

    const pending = this.supplyProviderOptionsRequests.get(contextKey);
    if (pending) return pending;

    this.loadingSupplyProviderOptionContexts.add(contextKey);
    const request = this.partsSelectionService
      .resolveOfferSupplierOptions({
        product_id: productId,
        selection_policy: automaticQuantity === undefined ? undefined : 'PRICE_BY_CITY',
        prefer_city_stock: true,
        warehouse_id: Number(this.warehouse?.id || this.user?.defaultWarehouse?.id || 0) || null,
        client_id: Number(this.client?.id || 0) || null,
        quantity: this.supplyProviderRequestQuantity(row, automaticQuantity)
      })
      .toPromise()
      .then((response) =>
        (response?.options || []).filter(
          (option) => Boolean(option.provider_id) && Number(option.quantity) > 0
        )
      )
      .catch((error) => {
        if (automaticQuantity !== undefined) throw error;
        return [];
      })
      .then((options) => {
        this.supplyProviderOptionsByContext.set(contextKey, options);
        return options;
      })
      .finally(() => {
        this.loadingSupplyProviderOptionContexts.delete(contextKey);
        this.supplyProviderOptionsRequests.delete(contextKey);
      });
    this.supplyProviderOptionsRequests.set(contextKey, request);
    return request;
  }

  private getSupplyProviderOptionsContextKey(row: AbstractControl, automaticQuantity?: number) {
    const productId = Number(row.value.product?.id || 0);
    if (!productId) return '';

    const warehouseId = Number(this.warehouse?.id || this.user?.defaultWarehouse?.id || 0);
    const clientId = Number(this.client?.id || 0);
    const quantity = this.supplyProviderRequestQuantity(row, automaticQuantity);
    return [productId, warehouseId, clientId, quantity, automaticQuantity === undefined ? 'manual' : 'automatic'].join(':');
  }


  private supplyProviderRequestQuantity(row: AbstractControl, automaticQuantity?: number) {
    const line = this.getEditableSupplyLine(row);
    return this.roundSupplyQuantity(Math.max(0.01,
      automaticQuantity ?? (line ? this.getSupplyCoverage(row, line).remaining : Number(row.value.quan || 1))));
  }

  getSupplyCoverage(row: AbstractControl, line: OrderSupplyLine) {
    const planned = this.getSupplyAllocations(row).controls.reduce(
      (sum, control) => sum + Math.max(Number(control.value.quantity || 0), 0),
      0
    );
    const warehouses = this.getWarehouseAllocations(row).controls.reduce(
      (sum, control) => sum + Math.max(Number(control.value.quantity || 0), 0),
      0
    );
    return orderSupplyCoverage(Number(row.value.quan ?? line.required), line, planned, warehouses);
  }

  isSupplyExpanded(row: AbstractControl) {
    return this.expandedSupplyRows.has(row);
  }

  get hasExpandedSupply() {
    return this.productsFormArray.controls.some((row) => this.isSupplyExpanded(row));
  }

  toggleSupplyExpanded(row: AbstractControl) {
    if (this.isSupplyExpanded(row)) {
      this.expandedSupplyRows.delete(row);
      if (this.warehousePickerRow === row) this.warehousePickerRow = null;
    } else {
      this.expandedSupplyRows.add(row);
    }
  }

  toggleAllSupplyExpanded() {
    const expand = !this.hasExpandedSupply;
    for (const row of this.productsFormArray.controls) {
      if (expand) this.expandedSupplyRows.add(row);
      else this.expandedSupplyRows.delete(row);
    }
    if (!expand) this.warehousePickerRow = null;
  }

  trackSupplySourceSummary(index: number) {
    return index;
  }

  getSupplySourceSummaries(row: AbstractControl, line: OrderSupplyLine): SupplySourceSummary[] {
    const sources: SupplySourceSummary[] = [];
    const quantity = (value: number) => `${this.formatQuantity(value)} шт.`;
    if (line.reserved > 0) {
      sources.push({ label: line.warehouse_name || this.warehouse?.description || 'Склад заказа',
        quantity: quantity(line.reserved), status: (line.picked || 0) > 0
          ? `Резерв · отобрано ${this.formatQuantity(line.picked || 0)}` : 'Резерв',
        icon: 'warehouse', kind: 'warehouse' });
    }
    if ((line.fulfilled || 0) > 0) {
      sources.push({ label: 'Отгружено', quantity: quantity(line.fulfilled || 0),
        status: 'Отгружено', icon: 'done_all', kind: 'shipped' });
    }
    const transfers = this.getTransferSources(row);
    for (const source of transfers) {
      sources.push({ label: `${source.source_warehouse} → ${source.target_warehouse}`,
        quantity: quantity(source.quantity), status: this.getTransferSourceStatus(source.status),
        icon: 'swap_horiz', kind: 'transfer', details: `Перемещение №${source.moving_doc_id}` });
    }
    if (this.hasTransferSupply(row) && !transfers.length) {
      sources.push({ label: 'Со своего склада', quantity: `${this.getTransferSupplyQuantity(row)} шт.`,
        status: this.getTransferSupplyStatus(row), icon: 'swap_horiz', kind: 'transfer',
        details: this.getTransferDocIds(row) ? `Перемещение №${this.getTransferDocIds(row)}` : '' });
    }
    const withoutDocument = this.getTransferWithoutDocumentQuantity(row);
    if (withoutDocument > 0) {
      sources.push({ label: 'Со своего склада', quantity: quantity(withoutDocument),
        status: 'Ожидает перемещения', icon: 'swap_horiz', kind: 'transfer', details: 'Документ ещё не создан' });
    }
    for (const allocation of this.getCommittedSupplyAllocations(row)) {
      sources.push({ label: allocation.provider, quantity: this.getCommittedSupplyQuantity(allocation),
        status: this.getCommittedSupplyStatus(allocation), icon: 'local_shipping', kind: 'supplier',
        ...this.getCommittedSupplierDeliverySummary(allocation),
        details: [this.getSupplyPurchasePriceLabel(allocation.purchase_price),
          allocation.contract_number ? `Договор №${allocation.contract_number}` : ''].filter(Boolean).join(' · ') });
    }
    for (const allocation of this.getWarehouseAllocations(row).controls) {
      const local = Number(allocation.value.warehouse_id) === Number(this.warehouse?.id);
      sources.push({ label: allocation.value.warehouse_name, quantity: quantity(allocation.value.quantity),
        status: local ? 'Резерв · черновик' : 'Перемещение · черновик',
        icon: local ? 'warehouse' : 'swap_horiz', kind: local ? 'warehouse' : 'transfer' });
    }
    for (const allocation of this.getSupplyAllocations(row).controls) {
      sources.push({ label: allocation.value.provider?.description || 'Поставщик не выбран',
        quantity: quantity(allocation.value.quantity), status: 'Черновик',
        icon: 'local_shipping', kind: 'supplier',
        ...this.getSupplierDeliverySummary(allocation.value.expectedAt, allocation.value.expectedTime,
          Boolean(allocation.value.expectedDateEstimated || allocation.value.expectedTimeEstimated)),
        details: this.getSupplyPurchasePriceLabel(allocation.value.purchasePrice) });
    }
    return sources;
  }

  private getCommittedSupplierDeliverySummary(allocation: OrderSupplyAllocation) {
    if (this.isCommittedSupplyReceived(allocation)) return {};
    const value = allocation.expected_at;
    const dateOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
    const expected = value && !dateOnly ? formatKyivDateTime(value) : { date: value || '', time: '' };
    return this.getSupplierDeliverySummary(expected.date,
      allocation.expected_at_precision === 'DATE' ? '' : expected.time,
      allocation.expected_at_confirmed === false);
  }

  private getSupplierDeliverySummary(dateValue: unknown, timeValue: unknown, estimated: boolean) {
    const date = this.formatSupplyExpectedDate(dateValue);
    const parsed = new Date(`${date}T00:00:00.000Z`);
    if (!date || !Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== date) {
      return { deliveryLabel: 'Срок?', deliveryDetails: 'Срок поставки не указан', deliveryEstimated: true };
    }
    const [year, month, day] = date.split('-');
    const time = /^([01]\d|2[0-3]):[0-5]\d$/.test(String(timeValue || '')) ? String(timeValue) : '';
    return {
      deliveryLabel: `${day}.${month}${time ? ` ${time}` : ''}`,
      deliveryDetails: `${estimated ? 'Ориентировочная поставка' : 'Поставка'}: ${day}.${month}.${year}${
        time ? ` до ${time} (Киев)` : ' · время не указано'}`,
      deliveryEstimated: estimated
    };
  }

  getSupplyPurchasePriceLabel(value: number | null | undefined) {
    const price = Number(value);
    return Number.isFinite(price) && price > 0
      ? `Закупочная цена: ${price.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн/шт.`
      : 'Закупочная цена не указана';
  }

  getCommittedSupplyAllocations(row: AbstractControl) {
    return (this.getSupplyLine(row)?.allocations || []).filter(
      (allocation) => allocation.status !== 'PLANNED' && allocation.status !== 'CANCELLED'
    );
  }

  canCancelSupplierAllocation(allocation: OrderSupplyAllocation) {
    return ['ORDERED', 'PARTIALLY_RECEIVED'].includes(allocation.status) &&
      orderSupplierReceipt(allocation).open > 0.0001 &&
      allocation.buy_ids?.length > 0;
  }

  async cancelSupplierAllocation(row: AbstractControl, allocation: OrderSupplyAllocation, buyId: number) {
    if (!this.id || this.saving || this.supplySaving || !this.canCancelSupplierAllocation(allocation)) return;
    let supplyChanged = false;
    this.supplySaving = true;
    try {
      const preview = await this.supplierCancellation.prepare(
        Number(this.id), Number(row.value.id), Number(allocation.id), Number(buyId)
      );
      const cancelled = await this.dialog.open(SupplierCancellationDialogComponent, {
        width: '520px', maxWidth: '95vw',
        data: { preview, provider: allocation.provider,
          product: row.value.product?.description || '', article: row.value.product?.article || '' }
      }).afterClosed().toPromise();
      if (!cancelled) return;
      this.markSourcesEdited(row);
      this.notificationService.notice(`Поставка от ${allocation.provider} отменена: ${this.formatQuantity(preview.quantity)} шт.`);
      // Refresh committed sources only: keep unsaved supplier and warehouse forms intact.
      try {
        const supply = await this.orderService.getSupply(Number(this.id)).toPromise();
        if (!supply) throw new Error('Пустой ответ сервера');
        this.supply = supply;
        supplyChanged = true;
        this.syncOrderExpectedAtFromSupply();
      } catch (error) {
        this.notificationService.warning('Поставка отменена, но не удалось обновить обеспечение заказа. Обновите страницу после сохранения ваших изменений.');
      }
    } catch (error) {
      this.notificationService.showError(extractApiErrorMessage(
        (error as any)?.error || error, 'Не удалось открыть отмену поставки'
      ));
    } finally {
      this.supplySaving = false;
      if (supplyChanged) this.refreshSupplierRetailPrice(row, true);
    }
  }

  canCancelTransferSource(source: OrderTransferSource) {
    return ['DRAFT', 'PICKING', 'READY_TO_DISPATCH'].includes(source.status) &&
      Number(source.quantity) - Number(source.received_quantity || 0) > 0.0001;
  }

  canCancelUndocumentedTransfer(row: AbstractControl) {
    return Number(this.getSupplyLine(row)?.transfer_pending || 0) > 0.0001 &&
      (!this.getTransferSources(row).length || this.getTransferWithoutDocumentQuantity(row) > 0.0001);
  }

  async cancelTransferSource(row: AbstractControl, source?: OrderTransferSource) {
    if (!this.id || !row.value.id || this.saving || this.supplySaving || this.supplyLoading ||
      !(source ? this.canCancelTransferSource(source) : this.canCancelUndocumentedTransfer(row))) return;
    this.supplySaving = true;
    try {
      const preview = await this.transferCancellation.prepare(Number(this.id), Number(row.value.id), source?.moving_doc_id);
      if (!preview) throw new Error('Не удалось загрузить перемещение');
      const cancelled = await this.dialog.open(TransferCancellationDialogComponent, {
        width: '540px', maxWidth: '95vw', data: { preview,
          product: row.value.product?.description || '', article: row.value.product?.article || '' }
      }).afterClosed().toPromise();
      if (!cancelled) return;
      this.markSourcesEdited(row);
      this.notificationService.notice(`Перемещение отменено: ${this.formatQuantity(preview.quantity)} шт.${
        preview.pickedQuantity > 0 || Number(preview.shelfReturnQuantity || 0) > 0 ? ' На складе создана задача возврата.' : ''}`);
      // Keep unsaved supplier and warehouse choices while refreshing committed sources.
      try {
        const supply = await this.orderService.getSupply(Number(this.id)).toPromise();
        if (!supply) throw new Error('Пустой ответ сервера');
        this.supply = supply;
        this.syncOrderExpectedAtFromSupply();
      } catch (error) {
        this.notificationService.warning('Перемещение отменено, но обеспечение не обновилось. Обновите страницу после сохранения ваших изменений.');
      }
    } catch (error) {
      this.notificationService.showError(extractApiErrorMessage(
        (error as any)?.error || error, 'Не удалось открыть отмену перемещения'
      ));
    } finally { this.supplySaving = false; }
  }

  getTransferWithoutDocumentQuantity(row: AbstractControl) {
    const line = this.getSupplyLine(row);
    if (!line?.transfer_pending || !line.transfer_sources?.length) return 0;
    const documented = line.transfer_sources
      .filter((source) => !['CANCELLED', 'ACCEPTED', 'RECEIVED'].includes(source.status))
      .reduce(
        (sum, source) =>
          sum + Math.max(Number(source.quantity) - Number(source.received_quantity), 0),
        0
      );
    return this.roundSupplyQuantity(Math.max(Number(line.transfer_pending) - documented, 0));
  }

  getTransferSources(row: AbstractControl) {
    return this.getSupplyLine(row)?.transfer_sources || [];
  }

  getTransferSourceStatus(status: string) {
    const labels: Record<string, string> = {
      DRAFT: 'Ожидает отбора',
      PICKING: 'Отбирается',
      READY_TO_DISPATCH: 'Готово к отправке',
      IN_TRANSIT: 'В пути',
      PARTIALLY_RECEIVED: 'Частично принято',
      RECEIVED: 'Принято',
      ACCEPTED: 'Принято',
      CANCELLED: 'Отменено',
      DISCREPANCY: 'Расхождение'
    };
    return labels[status] || status;
  }

  hasTransferSupply(row: AbstractControl) {
    const line = this.getSupplyLine(row);
    return Boolean(
      line &&
        (line.transfer_sources?.length ||
          Number(line.transfer_pending || 0) > 0 ||
          Number(line.transfer_reserved || 0) > 0 ||
          Number(line.transfer_in_transit || 0) > 0)
    );
  }

  getTransferSupplyStatus(row: AbstractControl) {
    const line = this.getSupplyLine(row);
    if (!line) return '';
    if (Number(line.transfer_in_transit || 0) > 0) return 'В пути';
    if (Number(line.transfer_picking || 0) > 0) return 'Отбирается на другом складе';
    if (Number(line.transfer_reserved || 0) > 0) return 'Зарезервировано на другом складе';
    return 'Ожидает организации перемещения';
  }

  getTransferSupplyQuantity(row: AbstractControl) {
    const line = this.getSupplyLine(row);
    return this.formatQuantity(
      line?.transfer_pending ??
        Number(line?.transfer_reserved || 0) + Number(line?.transfer_in_transit || 0)
    );
  }

  getTransferDocIds(row: AbstractControl) {
    return (this.getSupplyLine(row)?.moving_doc_ids || []).join(', ');
  }

  getCommittedSupplyStatus(allocation: OrderSupplyAllocation) {
    const receipt = orderSupplierReceipt(allocation);
    if (receipt.unreconciled > 0) return receipt.complete
      ? 'Принято, документы на сверке' : 'Частично принято, документы на сверке';
    if (this.isCommittedSupplyReceived(allocation)) return 'Получено';
    if (receipt.received > 0 || allocation.status === 'PARTIALLY_RECEIVED') return 'Частично получено';
    return 'Заказано';
  }

  isCommittedSupplyReceived(allocation: OrderSupplyAllocation) {
    return allocation.status === 'RECEIVED' || orderSupplierReceipt(allocation).complete;
  }

  getCommittedSupplyQuantity(allocation: OrderSupplyAllocation) {
    const receipt = orderSupplierReceipt(allocation);
    if (!this.isCommittedSupplyReceived(allocation) &&
      (receipt.received > 0 || allocation.status === 'PARTIALLY_RECEIVED')) {
      return `${this.formatQuantity(receipt.received)} из ${this.formatQuantity(
        allocation.quantity
      )} шт.`;
    }
    return `${this.formatQuantity(allocation.quantity)} шт.`;
  }

  canAddSupplyAllocation(row: AbstractControl, line: OrderSupplyLine) {
    return !this.saving && !this.supplySaving && this.getSupplyCoverage(row, line).remaining > 0;
  }

  private roundSupplyQuantity(value: number) {
    return Math.round(Number(value || 0) * 100) / 100;
  }

  private createSupplyAllocationForm(
    row: AbstractControl,
    provider?: Pick<Provider, 'id' | 'description'>,
    quantity = 1,
    purchasePrice?: number | null,
    autoSuggested = false,
    contract?: ContractWithProviderDoc | null,
    expectedAt?: Date | string | null,
    providerWarehouseId?: number | null,
    expectedEstimated = false
  ) {
    const expected = provider?.id
      ? this.resolveSupplyExpectedDateTime(expectedAt)
      : {
          date: this.formatSupplyExpectedDate(expectedAt),
          time: this.formatSupplyExpectedTime(expectedAt)
        };
    const group = this.fb.group({
      provider: new UntypedFormControl(provider || null),
      providerWarehouseId: new UntypedFormControl(providerWarehouseId || null),
      contract: new UntypedFormControl(contract || null),
      quantity: new UntypedFormControl(quantity),
      purchasePrice: new UntypedFormControl(purchasePrice ?? null, [
        Validators.required,
        Validators.min(0.01)
      ]),
      expectedAt: new UntypedFormControl(expected.date),
      expectedTime: new UntypedFormControl(expected.time),
      expectedDateEstimated: new UntypedFormControl(expectedEstimated || !this.formatSupplyExpectedDate(expectedAt)),
      expectedTimeEstimated: new UntypedFormControl(expectedEstimated || !this.formatSupplyExpectedTime(expectedAt)),
      autoSuggested: new UntypedFormControl(autoSuggested)
    });
    const markAsManual = () => {
      if (group.controls.autoSuggested.value) {
        group.controls.autoSuggested.setValue(false, { emitEvent: false });
      }
      this.markSourcesEdited(row);
    };
    group.controls.provider.valueChanges.subscribe((value) => {
      markAsManual();
      const option = this.getSupplyProviderOptions(row).find(
        (candidate) => Number(candidate.provider_id || 0) === Number(value?.id || 0)
      );
      group.controls.providerWarehouseId.setValue(option?.provider_warehouse_id || null, { emitEvent: false });
      group.controls.purchasePrice.setValue(option?.purchase_price ?? null, {
        emitEvent: false
      });
      group.controls.contract.setValue(null, { emitEvent: false });
      const expected = value?.id
        ? this.resolveSupplyExpectedDateTime(option?.expected_at)
        : { date: '', time: '' };
      group.controls.expectedAt.setValue(expected.date, {
        emitEvent: false
      });
      group.controls.expectedTime.setValue(expected.time, {
        emitEvent: false
      });
      group.controls.expectedDateEstimated.setValue(Boolean(value?.id && !this.formatSupplyExpectedDate(option?.expected_at)), { emitEvent: false });
      group.controls.expectedTimeEstimated.setValue(Boolean(value?.id && !this.formatSupplyExpectedTime(option?.expected_at)), { emitEvent: false });
      this.syncOrderExpectedAtFromSupply();
      this.refreshSupplierRetailPrice(row, true);
    });
    group.controls.quantity.valueChanges.subscribe(() => {
      markAsManual();
      this.syncOrderExpectedAtFromSupply();
      this.refreshSupplierRetailPrice(row, true);
    });
    group.controls.purchasePrice.valueChanges.subscribe(() => {
      markAsManual();
      this.refreshSupplierRetailPrice(row, true);
    });
    group.controls.contract.valueChanges.subscribe(() => {
      // Автоподстановка договора не превращает предложение поставщика в ручное.
      if (group.controls.contract.dirty) markAsManual();
    });
    group.controls.expectedAt.valueChanges.subscribe(() => {
      group.controls.expectedDateEstimated.setValue(false, { emitEvent: false });
      markAsManual();
      this.syncOrderExpectedAtFromSupply();
    });
    group.controls.expectedTime.valueChanges.subscribe(() => {
      group.controls.expectedTimeEstimated.setValue(false, { emitEvent: false });
      markAsManual();
      this.syncOrderExpectedAtFromSupply();
    });
    return group;
  }

  get supplierPricingPending() {
    return this.supplierPriceRequests.size > 0;
  }

  isSupplierPricePending(row: AbstractControl) {
    return this.supplierPriceRequests.has(row);
  }

  hasSupplierPriceError(row: AbstractControl) {
    return this.supplierPriceErrors.has(row);
  }

  private cancelSupplierRetailPrice(row: AbstractControl) {
    const request = this.supplierPriceRequests.get(row);
    if (request?.timer) clearTimeout(request.timer);
    this.supplierPriceRequests.delete(row);
    this.supplierPriceErrors.delete(row);
  }

  private keepManualRetailPrice(row: AbstractControl) {
    this.cancelSupplierRetailPrice(row);
    this.manuallyPricedRows.add(row);
  }

  private supplierPurchasePrice(row: AbstractControl): number | null {
    // PLANNED allocations are represented by the editable forms, not twice by the saved supply.
    const sources = [
      ...this.getSupplyAllocations(row).controls.map(source => ({
        providerId: Number(source.get('provider')?.value?.id || 0),
        quantity: Number(source.get('quantity')?.value),
        price: Number(source.get('purchasePrice')?.value)
      })),
      ...this.getCommittedSupplyAllocations(row).map(source => ({
        providerId: source.provider_id, quantity: Number(source.quantity), price: Number(source.purchase_price)
      }))
    ].filter(source => source.providerId > 0);
    if (!sources.length || sources.some(source => !Number.isFinite(source.quantity) || source.quantity <= 0 ||
      !Number.isFinite(source.price) || source.price <= 0)) return null;
    const quantity = sources.reduce((sum, source) => sum + source.quantity, 0);
    return Math.round(sources.reduce((sum, source) => sum + source.quantity * source.price, 0) / quantity * 100) / 100;
  }

  private supplierPricingContext() {
    return new Map(this.productsFormArray.controls.map(row => [row, JSON.stringify([
      this.client?.id || null, row.value.product?.id, row.value.quan, row.value.price,
      this.getSupplyAllocations(row).controls.map(source => [source.get('provider')?.value?.id,
        source.get('quantity')?.value, source.get('purchasePrice')?.value]),
      this.supplierPurchasePrice(row)
    ])]));
  }

  refreshSupplierRetailPrice(row: AbstractControl, sourceEdited = false) {
    this.cancelSupplierRetailPrice(row);
    if (sourceEdited) this.manuallyPricedRows.delete(row);
    if (this.destroyed || this.manuallyPricedRows.has(row) ||
      !this.productsFormArray.controls.includes(row)) return;
    // A single row price also belongs to already shipped units; keep their agreed price.
    if (Number(this.getSupplyLine(row)?.fulfilled || 0) > 0) return;
    const purchasePrice = this.supplierPurchasePrice(row);
    const productId = Number(row.value.product?.id || 0);
    if (!productId || purchasePrice === null) return;
    if (this.saving || this.supplySaving) {
      this.supplierPriceErrors.add(row);
      return;
    }
    const clientId = Number(this.client?.id || 0) || null;
    const expectedPrice = Number(row.value.price);
    const request: { timer?: ReturnType<typeof setTimeout> } = {};
    const current = () => !this.destroyed && !this.saving &&
      this.supplierPriceRequests.get(row) === request && this.productsFormArray.controls.includes(row) &&
      Number(row.value.product?.id) === productId && (Number(this.client?.id || 0) || null) === clientId &&
      this.supplierPurchasePrice(row) === purchasePrice && Number(row.value.price) === expectedPrice;
    this.supplierPriceRequests.set(row, request);
    request.timer = setTimeout(async () => {
      try {
        const response = await firstValueFrom(this.markupService.resolvePrice({
          product_id: productId, client_id: clientId, price: purchasePrice
        }).pipe(timeout(10000)));
        if (!current()) return;
        const price = Number(response?.price);
        if (!Number.isFinite(price) || price <= 0) throw new Error('Invalid retail price');
        row.patchValue({ price, sum: Math.round(price * Number(row.value.quan) * 100) / 100 });
        this.createForm.markAsDirty();
      } catch (error) {
        if (current()) {
          this.supplierPriceErrors.add(row);
          this.notificationService.warning('Не удалось пересчитать розничную цену. Повторите расчёт или укажите цену вручную');
        }
      } finally {
        if (this.supplierPriceRequests.get(row) === request) this.supplierPriceRequests.delete(row);
      }
    }, 150);
  }

  get automaticSupplyPending() {
    return Boolean(this.automaticSupplyPendingRows?.size);
  }

  isAutomaticSupplyPending(row: AbstractControl) {
    return Boolean(this.automaticSupplyPendingRows?.has(row));
  }

  private cancelAutomaticSupply(row: AbstractControl) {
    const timer = this.automaticSupplyTimers?.get(row);
    if (timer) clearTimeout(timer);
    this.automaticSupplyTimers?.delete(row);
    this.automaticSupplyPendingRows?.delete(row);
  }

  private clearAutomaticSupplySuggestions() {
    for (const row of this.automaticSupplyPendingRows || []) {
      this.bumpDraftSupplySuggestionVersion(row);
      this.cancelAutomaticSupply(row);
    }
  }

  private scheduleAutomaticSupply(row: AbstractControl) {
    const version = this.bumpDraftSupplySuggestionVersion(row);
    this.cancelAutomaticSupply(row);
    if (!Number.isFinite(Number(row.value.quan)) || Number(row.value.quan) <= 0) return;
    this.automaticSupplyPendingRows.add(row);
    this.automaticSupplyTimers.set(row, setTimeout(() => {
      this.automaticSupplyTimers.delete(row);
      void this.runAutomaticSupply(row, version);
    }, 250));
  }

  private runAutomaticSupply(row: AbstractControl, version = this.bumpDraftSupplySuggestionVersion(row)) {
    this.automaticSupplyPendingRows.add(row);
    const request = this.automaticSupplyQueue.then(() => this.reconcileAutomaticSupply(row, version))
      .catch(() => {
        if (this.draftSupplySuggestionVersions.get(row) === version && !this.automaticSupplyDisposed) {
          this.notificationService.warning('Не удалось автоматически подобрать обеспечение. Повторите изменение количества или выберите источники вручную');
        }
      }).finally(() => {
        if (this.draftSupplySuggestionVersions.get(row) === version) this.automaticSupplyPendingRows.delete(row);
      });
    this.automaticSupplyQueue = request;
    return request;
  }

  private automaticSupplyContext(row: AbstractControl) {
    return [row.value.product?.id, row.value.quan, this.warehouse?.id, this.client?.id,
      this.user?.defaultWarehouse?.id].join(':');
  }

  private async reconcileAutomaticSupply(row: AbstractControl, version: number) {
    const context = this.automaticSupplyContext(row);
    const current = () => !this.automaticSupplyDisposed && !this.saving && !this.supplySaving &&
      this.productsFormArray.controls.includes(row) &&
      this.draftSupplySuggestionVersions.get(row) === version && this.automaticSupplyContext(row) === context;
    if (!current() || this.supplyLoading || !Number.isFinite(Number(row.value.quan)) || Number(row.value.quan) <= 0) return;
    const refreshed = await this.refreshWarehouseOptions();
    if (!current()) return;
    if (!refreshed) throw new Error('Warehouse availability unavailable');
    const line = this.getEditableSupplyLine(row);
    if (!line) return;
    const warehouses = this.getWarehouseAllocations(row);
    const suppliers = this.getSupplyAllocations(row);
    const manualQuantity = [...warehouses.controls, ...suppliers.controls]
      .filter(control => !control.value.autoSuggested)
      .reduce((sum, control) => sum + Math.max(Number(control.value.quantity || 0), 0), 0);
    const committed = orderSupplyCoverage(Number(row.value.quan), line, 0, 0).committed;
    let remaining = this.roundSupplyQuantity(Math.max(Number(row.value.quan) - committed - manualQuantity, 0));
    const productId = Number(row.value.product?.id);
    const assignedWarehouses = new Map<number, number>();
    for (const other of this.productsFormArray.controls) {
      if (Number(other.value.product?.id) !== productId) continue;
      for (const source of this.getWarehouseAllocations(other).controls) {
        if (other === row && source.value.autoSuggested) continue;
        const id = Number(source.value.warehouse_id);
        assignedWarehouses.set(id, (assignedWarehouses.get(id) || 0) + Math.max(Number(source.value.quantity || 0), 0));
      }
    }
    const preferredWarehouseId = Number(this.user?.defaultWarehouse?.id || this.warehouse?.id);
    const warehousePlan = allocateAvailableWarehouseStock(remaining, this.getWarehouseOptions(row)
      .filter(option => !line.excluded_warehouse_ids?.includes(option.warehouse_id) &&
        this.getPendingStockRelease(row)?.warehouse_id !== option.warehouse_id)
      .map(option => ({ ...option, is_target: option.warehouse_id === preferredWarehouseId })), assignedWarehouses);
    remaining = this.roundSupplyQuantity(remaining - warehousePlan.reduce((sum, source) => sum + source.quantity, 0));
    const supplierPlan: Array<{ option: PartsSelectionOfferSupplierOption; quantity: number }> = [];
    if (remaining > 0) {
      const options = await this.ensureSupplyProviderOptions(row, remaining);
      if (!current()) return;
      const assignedSuppliers = new Map<string, number>();
      for (const other of this.productsFormArray.controls) {
        if (Number(other.value.product?.id) !== productId) continue;
        for (const source of this.getSupplyAllocations(other).controls) {
          if (other === row && source.value.autoSuggested) continue;
          const key = `${Number(source.value.provider?.id || 0)}:${Number(source.value.providerWarehouseId || 0)}`;
          assignedSuppliers.set(key, (assignedSuppliers.get(key) || 0) + Math.max(Number(source.value.quantity || 0), 0));
        }
      }
      for (const option of [...options].sort((a, b) =>
        Number(a.availability_kind !== 'IN_CITY') - Number(b.availability_kind !== 'IN_CITY') ||
        Number(a.purchase_price) - Number(b.purchase_price) || Number(a.provider_id) - Number(b.provider_id) ||
        Number(a.provider_warehouse_id || 0) - Number(b.provider_warehouse_id || 0))) {
        if (remaining <= 0) break;
        if (!option.provider_id || !Number.isFinite(Number(option.purchase_price)) || Number(option.purchase_price) <= 0) continue;
        const key = `${option.provider_id}:${option.provider_warehouse_id || 0}`;
        const assigned = (assignedSuppliers.get(key) || 0) +
          (option.provider_warehouse_id ? assignedSuppliers.get(`${option.provider_id}:0`) || 0 : 0);
        const available = this.roundSupplyQuantity(Math.max(Number(option.quantity || 0) - assigned, 0));
        const quantity = Math.min(remaining, available);
        if (!Number.isFinite(quantity) || quantity <= 0) continue;
        supplierPlan.push({ option, quantity });
        assignedSuppliers.set(key, (assignedSuppliers.get(key) || 0) + quantity);
        remaining = this.roundSupplyQuantity(remaining - quantity);
      }
    }
    if (!current()) return;
    for (const form of [warehouses, suppliers]) {
      for (let index = form.length - 1; index >= 0; index--) {
        if (form.at(index).value.autoSuggested) form.removeAt(index, { emitEvent: false });
      }
    }
    for (const source of warehousePlan) {
      warehouses.push(this.createWarehouseAllocation(row, source, true), { emitEvent: false });
    }
    for (const { option, quantity } of supplierPlan) {
      suppliers.push(this.createSupplyAllocationForm(row,
        { id: Number(option.provider_id), description: option.provider_name || '' }, quantity,
        option.purchase_price, true, null, option.expected_at, option.provider_warehouse_id), { emitEvent: false });
    }
    this.initializedWarehouseRows.add(row);
    this.warehouseSourcesChanged = true;
    this.createForm.markAsDirty();
    this.syncOrderExpectedAtFromSupply();
    this.refreshSupplierRetailPrice(row);
  }

  async suggestDraftSupplyForRow(row: AbstractControl, replaceAuto = false) {
    if (this.editedSourceRows.has(row)) return;
    if (!replaceAuto && this.getSupplyAllocations(row).length) return;
    await this.runAutomaticSupply(row);
  }

  private bumpDraftSupplySuggestionVersion(row: AbstractControl) {
    const next = Number(this.draftSupplySuggestionVersions.get(row) || 0) + 1;
    this.draftSupplySuggestionVersions.set(row, next);
    return next;
  }

  addSupplyAllocation(row: AbstractControl) {
    const line = this.getEditableSupplyLine(row);
    const quantity = line ? this.getSupplyCoverage(row, line).remaining : 0;
    if (quantity <= 0 || this.saving) return;
    this.expandedSupplyRows.add(row);
    this.markSourcesEdited(row);
    this.getSupplyAllocations(row).push(this.createSupplyAllocationForm(row, undefined, quantity));
    this.syncOrderExpectedAtFromSupply();
  }

  removeSupplyAllocation(row: AbstractControl, index: number) {
    if (this.saving) return;
    this.markSourcesEdited(row);
    this.getSupplyAllocations(row).removeAt(index);
    this.syncOrderExpectedAtFromSupply();
    this.refreshSupplierRetailPrice(row, true);
  }

  get hasPlannedSupply() {
    return [...this.supplyForms.values()].some((array) => array.length > 0);
  }

  getSupplyAllocationGroup(allocation: AbstractControl): UntypedFormGroup {
    return allocation as UntypedFormGroup;
  }

  private serializeSupplyAllocations(row: AbstractControl) {
    return this.getSupplyAllocations(row)
      .controls.filter(
        (allocation) =>
          Number(allocation.value.provider?.id || 0) > 0 &&
          Number(allocation.value.quantity || 0) > 0
      )
      .map((allocation) => ({
        provider_id: Number(allocation.value.provider.id),
        contract_id: Number(allocation.value.contract?.id || 0) || undefined,
        provider_warehouse_id: Number(allocation.value.providerWarehouseId || 0) || undefined,
        quantity: Number(allocation.value.quantity),
        expected_at:
          this.serializeSupplyExpectedDateTime(
            allocation.value.expectedAt,
            allocation.value.expectedTime
          ) || undefined,
        purchase_price:
          allocation.value.purchasePrice === null ||
          allocation.value.purchasePrice === undefined ||
          allocation.value.purchasePrice === ''
            ? undefined
            : Number(allocation.value.purchasePrice)
      }));
  }

  private validateSupplyPurchasePrices(): boolean {
    for (const row of this.productsFormArray.controls) {
      for (const allocation of this.getSupplyAllocations(row).controls) {
        const providerId = Number(allocation.value.provider?.id || 0);
        const quantity = Number(allocation.value.quantity || 0);
        if (!providerId || quantity <= 0) continue;

        const numeric = Number(allocation.value.purchasePrice);
        const purchasePrice = Number.isFinite(numeric)
          ? Math.round(numeric * 100) / 100
          : Number.NaN;
        if (Number.isFinite(purchasePrice) && purchasePrice > 0) continue;

        allocation.get('purchasePrice')?.markAsTouched();
        this.expandedSupplyRows.add(row);
        const productName = row.value.product?.description || `строки ${row.value.id || ''}`;
        this.notificationService.warning(
          `Укажите закупочную цену больше нуля для «${productName}»`
        );
        return false;
      }
    }

    return true;
  }

  private resolveSupplyExpectedDateTime(value?: unknown) {
    const date = this.formatSupplyExpectedDate(value);
    if (date) return { date, time: this.formatSupplyExpectedTime(value) || '17:00' };
    const tomorrow = kyivWallTime(new Date());
    tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
    return { date: tomorrow.toISOString().slice(0, 10), time: '17:00' };
  }

  private formatSupplyExpectedDate(value?: unknown) {
    if (!value) return '';

    if (typeof value === 'string') {
      const dateOnly = value.match(/^(\d{4}-\d{2}-\d{2})(?:$|T)/)?.[1];
      if (dateOnly) return dateOnly;
    }

    if (
      typeof value === 'object' &&
      'format' in value &&
      typeof (value as { format?: unknown }).format === 'function'
    ) {
      const formatted = (value as { format: (pattern: string) => string }).format('YYYY-MM-DD');
      return /^\d{4}-\d{2}-\d{2}$/.test(formatted) ? formatted : '';
    }

    const date =
      value instanceof Date
        ? value
        : typeof value === 'string' || typeof value === 'number'
        ? new Date(value)
        : null;
    if (!date) return '';
    if (Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatSupplyExpectedTime(value?: unknown) {
    if (!value || (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value))) return '';

    if (
      typeof value === 'object' &&
      'format' in value &&
      typeof (value as { format?: unknown }).format === 'function'
    ) {
      const formatted = (value as { format: (pattern: string) => string }).format('HH:mm');
      return /^\d{2}:\d{2}$/.test(formatted) ? formatted : '';
    }

    const date =
      value instanceof Date
        ? value
        : typeof value === 'string' || typeof value === 'number'
        ? new Date(value)
        : null;
    if (!date || Number.isNaN(date.getTime())) return '';
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  private serializeSupplyExpectedDateTime(dateValue: unknown, timeValue: unknown) {
    const date = this.formatSupplyExpectedDate(dateValue);
    if (!date) return null;
    const time = /^\d{2}:\d{2}$/.test(String(timeValue || '')) ? String(timeValue) : '00:00';
    const localDateTime = new Date(`${date}T${time}:00`);
    return Number.isNaN(localDateTime.getTime()) ? null : localDateTime.toISOString();
  }

  private getLatestSupplyExpectedAt(): Date | null {
    const readiness = this.editableOrderReadiness;
    return readiness.expected_at ? new Date(readiness.expected_at) : null;
  }

  private syncOrderExpectedAtFromSupply() {
    const expectedAt = this.getLatestSupplyExpectedAt();
    if (!expectedAt) return;
    this.dateExpectedFormControl.setValue(expectedAt, { emitEvent: false });
    this.timeExpectedFormControl.setValue(
      `${String(expectedAt.getHours()).padStart(2, '0')}:${String(expectedAt.getMinutes()).padStart(
        2,
        '0'
      )}`,
      { emitEvent: false }
    );
  }

  private buildSupplyContract(
    id: number,
    number: string | null | undefined,
    provider: Pick<Provider, 'id' | 'description'>
  ): ContractWithProviderDoc {
    return {
      id,
      number: number || '',
      provider: provider as Provider,
      // The supply response does not contain the contract's organization.
      // Do not label it with the operator's unrelated default organization.
      org: '',
      start_date: new Date(0),
      end_date: new Date(0),
      day_limit: 0,
      credit_limit: 0,
      active: true
    };
  }

  async saveSupplyPlan(showNotice = true) {
    if (!this.id || !this.supply || this.saving || this.supplySaving) return false;
    if (this.warehouseChangePending) {
      this.notificationService.warning('Сначала сохраните весь заказ: смена склада и изменения обеспечения сохраняются вместе');
      return false;
    }
    if (
      this.hasWarehouseDrafts ||
      this.warehouseSourcesChanged ||
      this.productsFormArray.controls.some((row) => !row.value.id)
    ) {
      this.notificationService.warning(
        'Сначала сохраните весь заказ: новые складские источники и товары оформляются вместе с заказом'
      );
      return false;
    }
    if (!this.validateSupplyPurchasePrices() || !this.validateSourceQuantities()) return false;
    const lines = this.supply.lines.map((line) => {
      const productControl = this.productsFormArray.controls.find(
        (control) => Number(control.value.id) === Number(line.order_product_id)
      );
      const form = productControl ? this.supplyForms.get(productControl) : undefined;
      return {
        order_product_id: line.order_product_id,
        allocations: (form?.controls || [])
          .filter((control) => control.value.provider?.id && Number(control.value.quantity) > 0)
          .map((control) => ({
            provider_id: Number(control.value.provider.id),
            contract_id: Number(control.value.contract?.id || 0) || undefined,
            quantity: Number(control.value.quantity),
            expected_at:
              this.serializeSupplyExpectedDateTime(
                control.value.expectedAt,
                control.value.expectedTime
              ) || undefined,
            purchase_price:
              control.value.purchasePrice === null ||
              control.value.purchasePrice === undefined ||
              control.value.purchasePrice === ''
                ? undefined
                : Number(control.value.purchasePrice)
          }))
      };
    });
    this.supplySaving = true;
    try {
      this.supply = await this.orderService.saveSupplyPlan(this.id, { lines }).toPromise();
      if (showNotice) this.notificationService.notice('План закупки сохранён');
      await this.loadSupply();
      return true;
    } catch (error) {
      console.error(error);
      this.notificationService.showError(
        extractApiErrorMessage(error, 'Не удалось сохранить план закупки')
      );
      return false;
    } finally {
      this.supplySaving = false;
    }
  }

  async confirmSupplyOrdered() {
    if (!this.id || this.saving || this.supplySaving) return;
    if (!(await this.saveSupplyPlan(false))) return;
    if (!this.supply) return;
    return this.confirmPlannedSupplierOrders(this.id, this.supply);
  }

  private async confirmPlannedSupplierOrders(
    orderId: number,
    supply: OrderSupplyResponse
  ): Promise<boolean> {
    const planned = supply.lines.reduce(
      (result, line) => [
        ...result,
        ...line.allocations.filter((allocation) => allocation.status === 'PLANNED')
      ],
      [] as OrderSupplyAllocation[]
    );
    if (!planned.length) {
      this.notificationService.warning('Нет черновиков заказов поставщикам');
      return false;
    }
    const total = planned.reduce((sum, allocation) => sum + Number(allocation.quantity), 0);
    if (!window.confirm(`Отметить заказанными черновики поставщикам: ${total} шт.?`)) return false;
    this.supplySaving = true;
    const operationId = this.uuid();
    const providerContracts: Array<{ provider_id: number; contract_id: number }> = [];
    try {
      let result;
      while (!result) {
        try {
          result = await this.orderService
            .confirmProcurement(
              orderId,
              planned.map((allocation) => allocation.id),
              providerContracts,
              operationId
            )
            .toPromise();
        } catch (error) {
          const response = (error as any)?.error;
          const details = response?.code ? response : response?.message;
          if (details?.code !== 'CONTRACT_SELECTION_REQUIRED') throw error;
          const contracts = details.contracts || [];
          const choice = window.prompt(
            `Выберите договор:\n${contracts
              .map((contract: any) => `${contract.id} — ${contract.number}`)
              .join('\n')}\n\nВведите номер слева:`
          );
          const contractId = Number(choice);
          if (!contracts.some((contract: any) => Number(contract.id) === contractId)) {
            this.notificationService.warning('Заказ поставщику не создан');
            return false;
          }
          providerContracts.push({
            provider_id: Number(details.provider_id),
            contract_id: contractId
          });
        }
      }
      if (!result) throw new Error('Пустой ответ сервера');
      this.supply = result.supply;
      this.notificationService.notice(
        `Отмечено заказанными документов поставщикам: ${result.buy_ids.length}`
      );
      await this.loadSupply();
      return true;
    } catch (error) {
      console.error(error);
      this.notificationService.showError(
        extractApiErrorMessage(error, 'Не удалось подтвердить заказ поставщикам')
      );
      return false;
    } finally {
      this.supplySaving = false;
    }
  }

  openDialog(): void {
    if (this.destroyed || this.productDialogOpen || this.saving || this.supplySaving) return;
    this.productDialogOpen = true;
    const dialogRef = this.dialog.open(SelectorComponent, { width: '2000px' });
    const selections = new Subscription();
    this.productDialogSubscriptions.add(selections);
    let choosing = false;
    let closed = false;
    dialogRef.componentInstance.keyboardMode = true;
    dialogRef.componentInstance.docType = DocTypeEnum.order;
    dialogRef.componentInstance.controls = this.productsFormArray;
    dialogRef.componentInstance.defaultWarehouse = this.warehouse || this.user?.defaultWarehouse;
    dialogRef.componentInstance.changeRow = this.changeRow.bind(this);
    selections.add(dialogRef.componentInstance.keyboardProductChosen.subscribe(async (product: Product) => {
      if (choosing || closed || this.destroyed) return;
      choosing = true;
      try {
        await this.addProduct(product);
        if (closed || this.destroyed) return;
        const index = this.getIndexOfRow(product);
        if (index >= 0) dialogRef.close({ keyboardProductIndex: index });
      } finally {
        choosing = false;
        if (!closed && !this.destroyed) dialogRef.componentInstance.releaseKeyboardSelection();
      }
    }));
    selections.add(dialogRef.componentInstance.toggleProductEvent.subscribe((product) => {
      void this.toggleProduct(product);
    }));
    selections.add(dialogRef.componentInstance.toggleAllProductEvent.subscribe((products) => {
      void this.toggleAllProduct(products);
    }));
    selections.add(dialogRef.afterClosed().subscribe((result) => {
      closed = true;
      this.productDialogOpen = false;
      selections.unsubscribe();
      this.productDialogSubscriptions.remove(selections);
      if (!this.destroyed) this.focusProductsAfterRender(result?.keyboardProductIndex ?? this.activeProductIndex);
    }));
  }

  selectProduct(selectedProducts: Product) {
    this.selectedProducts = [
      ...this.selectedProducts,
      {
        quan: 1,
        price: selectedProducts.price,
        product: selectedProducts,
        sum: selectedProducts.price
      }
    ];
  }

  async addProduct(product: Product): Promise<void> {
    const productId = Number(product.id);
    if (this.findProductControl(product) || this.pendingProductIds.has(productId)) {
      return;
    }
    this.pendingProductIds.add(productId);
    try {
      const row = await this.createNewOrderProductRow(product);
      if (this.findProductControl(product)) {
        return;
      }
      const control = this.createProductInput(row);
      this.productsFormArray.push(control);
      this.updateView();
      await this.suggestDraftSupplyForRow(control, true);
    } catch (error) {
      console.error(error);
      this.notificationService.warning('Не удалось рассчитать цену товара');
    } finally {
      this.pendingProductIds.delete(productId);
    }
  }

  private async createNewOrderProductRow(product: Product): Promise<ProductInDoc> {
    const source = this.resolveProductPriceSource(product);
    const price = await this.markupService.getClientRetailPriceAsync(
      product,
      source.basePrice,
      this.client?.id
    );
    const provider = this.hasCurrentWarehouseStock(product) ? undefined : source.provider;

    return {
      quan: 1,
      price,
      product,
      sum: price,
      provider
    };
  }

  private resolveProductPriceSource(product: Product): OrderProductPriceSource {
    const supplier = this.findBestSupplierCandidate(product);
    if (supplier) {
      return {
        basePrice: Number(supplier.price),
        provider: supplier.providerWarehouse.provider
      };
    }

    return {
      basePrice: this.resolveFallbackProductPrice(product)
    };
  }

  private findBestSupplierCandidate(product: Product): ProductInProviderWarehouse | undefined {
    const candidates = (product.providerStock || []).filter((row) => {
      return (
        Number(row.quan || 0) > 0 &&
        Number(row.price || 0) > 0 &&
        Boolean(row.providerWarehouse?.provider?.id)
      );
    });
    if (!candidates.length) return undefined;

    const cityId = this.getCurrentCityId();
    const inCity = cityId
      ? candidates.filter((row) => Number(row.providerWarehouse?.city?.id || 0) === cityId)
      : [];
    const source = inCity.length ? inCity : candidates;

    return [...source].sort((left, right) => {
      const priceDiff = Number(left.price || 0) - Number(right.price || 0);
      if (priceDiff !== 0) return priceDiff;
      return Number(left.providerWarehouse?.id || 0) - Number(right.providerWarehouse?.id || 0);
    })[0];
  }

  private resolveFallbackProductPrice(product: Product) {
    const price = Number(product.price || 0);
    if (price > 0) return price;

    const retail = Number(product.retail || 0);
    return retail > 0 ? retail : 0;
  }

  private hasCurrentWarehouseStock(product: Product) {
    const warehouseId = this.getCurrentWarehouseId();
    if (!warehouseId) return false;

    return (product.stock || []).some((row) => {
      return Number(row.warehouse?.id || 0) === warehouseId && Number(row.quan || 0) > 0;
    });
  }

  private getCurrentWarehouseId() {
    return Number(this.warehouse?.id || this.user?.defaultWarehouse?.id || 0);
  }

  private getCurrentCityId() {
    return Number(this.warehouse?.city?.id || this.user?.defaultWarehouse?.city?.id || 0);
  }

  getProviderControl(prod: UntypedFormGroup) {
    return prod.controls.provider as UntypedFormControl;
  }

  getIndexOfRow(product: Product) {
    return this.productsFormArray.value.findIndex(
      (prod: ProductInDoc) => prod.product.id === product.id
    );
  }

  getSum() {
    let sum = 0;
    for (let index = 0; index < this.products.length; index++) {
      const prod = this.products[index].value;
      sum += Number(prod.quan * prod.price);
    }
    return sum;
  }

  getSupplyStatusLabel() {
    return getOrderSupplyStatusLabel(this.order?.status_summary?.supply_status);
  }

  getPaymentStatusLabel() {
    return getOrderPaymentStatusLabel(
      this.order?.status_summary?.payment_status,
      this.order?.status_summary
    );
  }

  getShipmentStatusLabel() {
    return getOrderShipmentStatusLabel(this.order?.status_summary?.shipment_status);
  }

  hasReturnedProducts() {
    return this.productsInDoc.some((row: ProductInDoc) => Number(row.returned_quan || 0) > 0);
  }

  getReturnedProductsCount() {
    return this.productsInDoc.filter((row: ProductInDoc) => Number(row.returned_quan || 0) > 0)
      .length;
  }

  formatQuantity(value: number | undefined) {
    const normalized = Number(value || 0);
    return Number.isInteger(normalized)
      ? String(normalized)
      : normalized.toFixed(2).replace(/\.?0+$/, '');
  }

  getReturnDocIdsLabel(ids?: number[]) {
    if (!Array.isArray(ids) || !ids.length) return '';
    return ids
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0)
      .map((id) => `#${id}`)
      .join(', ');
  }

  async removeProduct(row: AbstractControl | number): Promise<void> {
    const control = typeof row === 'number' ? this.productsFormArray.at(row) : row;
    if (control) await this.removeProducts([control]);
  }

  private async removeProducts(controls: AbstractControl[]): Promise<void> {
    if (this.saving || this.supplySaving || this.supplyLoading || this.removeConfirmationPending || this.destroyed) return;
    const rows = [...new Set(controls)].filter(row => this.productsFormArray.controls.includes(row));
    if (!rows.length) return;
    if (this.blockRemovalRequiringSupplyAction(rows)) return;
    const orderId = this.id;
    const persisted = rows.filter(row => Number(row.value.id) > 0);
    if (persisted.length) {
      this.removeConfirmationPending = true;
      try {
        const confirmed = await this.dialog.open(this.removeProductsDialog, {
          width: '520px', maxWidth: '95vw', ariaLabel: 'Удаление товаров из заказа',
          data: { count: rows.length, product: rows.length === 1 ? rows[0].value.product?.description : '' }
        }).afterClosed().toPromise();
        if (confirmed !== true) return;
      } finally {
        this.removeConfirmationPending = false;
      }
    }
    // Keep the selected row identity if the table was reordered while the dialog was open.
    if (this.id !== orderId || this.saving || this.supplySaving || this.supplyLoading || this.destroyed) return;
    const remainingRows = rows.filter(row => this.productsFormArray.controls.includes(row));
    if (!remainingRows.length) return;
    if (this.blockRemovalRequiringSupplyAction(remainingRows)) return;
    const activeRow = this.products[this.activeProductIndex];
    for (const row of remainingRows) {
      const index = this.productsFormArray.controls.indexOf(row);
      if (index < 0) continue;
      this.bumpDraftSupplySuggestionVersion(row);
      this.cancelAutomaticSupply(row);
      this.cancelSupplierRetailPrice(row);
      this.supplyForms.delete(row);
      this.warehouseForms.delete(row);
      this.pendingStockReleases.delete(row);
      this.expandedSupplyRows.delete(row);
      this.initializedWarehouseRows.delete(row);
      this.editedSourceRows.delete(row);
      if (this.warehousePickerRow === row) this.warehousePickerRow = null;
      if (Number(row.value.id) > 0) this.removedProductsCount++;
      this.productsFormArray.removeAt(index);
    }
    this.activeProductIndex = Math.max(0, this.products.indexOf(activeRow));
    this.createForm.markAsDirty();
    this.hideProductImagePreview();
    this.syncOrderExpectedAtFromSupply();
    this.updateView();
  }

  private blockRemovalRequiringSupplyAction(rows: AbstractControl[]): boolean {
    for (const row of rows) {
      if (!Number(row.value.id)) continue;
      const line = this.getSupplyLine(row);
      const awaitingReconciliation = Number(line?.unattributed_received_quantity || 0) > 0.0001 ||
        (line?.allocations || []).some((allocation) => orderSupplierReceipt(allocation).unreconciled > 0.0001);
      const partiallyReceived = (line?.allocations || []).some((allocation) =>
        allocation.status === 'PARTIALLY_RECEIVED' ||
        ['PLANNED', 'ORDERED'].includes(allocation.status) && Number(allocation.received_quantity || 0) > 0.0001);
      if (!awaitingReconciliation && !partiallyReceived) continue;
      this.expandedSupplyRows.add(row);
      this.notificationService.warning(awaitingReconciliation
        ? 'Сначала завершите сверку приёмки этой позиции'
        : 'По позиции уже получена часть закупки. Сначала обработайте полученный товар и отмените остаток отдельно');
      this.focusSupplyRow(Number(row.value.product?.id));
      return true;
    }
    return false;
  }

  async toggleProduct(product: Product): Promise<void> {
    const indexOfProduct = this.getIndexOfRow(product);
    if (indexOfProduct >= 0) {
      await this.removeProduct(indexOfProduct);
    } else {
      await this.addProduct(product);
    }
  }

  async toggleAllProduct(products: Product[]): Promise<void> {
    if (this.isAllSelected(products)) {
      await this.removeProducts([...this.productsFormArray.controls]);
    } else {
      await Promise.all(
        products.map((prod) => {
          if (!this.findProductControl(prod)) {
            return this.addProduct(prod);
          }
          return Promise.resolve();
        })
      );
    }
  }

  isAllSelected(products: Product[]) {
    return products.every((product) => Boolean(this.findProductControl(product)));
  }

  changeRow(product: AbstractControl, type: string) {
    switch (type) {
      case 'quan':
        product.patchValue({
          sum: Math.round(product.value.price * product.value.quan * 100) / 100,
          quan: product.value.quan
        });
        this.createForm.markAsDirty();
        this.scheduleAutomaticSupply(product);
        break;
      case 'price':
        this.keepManualRetailPrice(product);
        product.patchValue({
          sum: Math.round(product.value.price * product.value.quan * 100) / 100,
          price: product.value.price
        });
        break;
      case 'sum':
        this.keepManualRetailPrice(product);
        product.patchValue({
          price: Math.round((product.value.sum / product.value.quan) * 100) / 100
        });
        break;
    }
  }

  onProductArticleCopied(copied: boolean, article: string) {
    if (copied) {
      this.notificationService.notice(`Артикул ${article} скопирован`);
    } else {
      this.notificationService.warning('Не удалось скопировать артикул');
    }
  }

  getProductBrandLogoUrl(product?: Product | null): string | null {
    const logo = String(product?.brand?.logo || '').trim();
    if (!logo) return null;
    const logoUrl = `${environment.BACKEND}/image/${logo}`;
    return this.failedProductBrandLogoUrls.has(logoUrl) ? null : logoUrl;
  }

  onProductBrandLogoError(logoUrl: string) {
    this.failedProductBrandLogoUrls.add(logoUrl);
  }

  getProductImageUrl(product?: Product | null): string | null {
    const image = product?.images?.[0];
    const imageName = String(image?.name || '').trim();
    const directUrl = String(image?.url || '').trim();
    let imageUrl: string | null = null;

    if (/^(https?:|data:image\/)/i.test(imageName)) {
      imageUrl = imageName;
    } else if (imageName && !imageName.startsWith('supplier-remote-')) {
      imageUrl = `${environment.BACKEND}/image/${imageName}`;
    } else if (/^(https?:|data:image\/)/i.test(directUrl)) {
      imageUrl = this.getPublicSupplierImageUrl(directUrl) || directUrl;
    } else if (imageName) {
      imageUrl = `${environment.BACKEND}/image/${imageName}`;
    }

    return imageUrl && !this.failedProductImageUrls.has(imageUrl) ? imageUrl : null;
  }

  showProductImagePreview(event: MouseEvent, imageUrl: string, title?: string | null) {
    this.hoveredProductImageUrl = imageUrl;
    this.hoveredProductImageTitle = title || '';
    this.updateProductImagePreviewPosition(event);
  }

  moveProductImagePreview(event: MouseEvent) {
    if (!this.hoveredProductImageUrl) return;
    this.updateProductImagePreviewPosition(event);
  }

  hideProductImagePreview() {
    this.hoveredProductImageUrl = null;
    this.hoveredProductImageTitle = '';
  }

  onProductImageError(imageUrl: string | null) {
    if (!imageUrl) return;
    this.failedProductImageUrls.add(imageUrl);
    if (this.hoveredProductImageUrl === imageUrl) this.hideProductImagePreview();
  }

  private getPublicSupplierImageUrl(imageUrl: string): string | null {
    const match = imageUrl.match(/^https?:\/\/ecom\.ad\.ua\/tcd\/(.+)$/i);
    return match?.[1] ? `https://img2.ad.ua/imgs/tcd-pic/${match[1]}` : null;
  }

  private updateProductImagePreviewPosition(event: MouseEvent) {
    const viewportPadding = 12;
    const maxLeft = Math.max(
      viewportPadding,
      window.innerWidth - this.productImagePreviewSizePx - viewportPadding
    );
    const maxTop = Math.max(
      viewportPadding,
      window.innerHeight - this.productImagePreviewSizePx - viewportPadding
    );

    this.hoveredProductImagePreviewLeft = Math.min(
      Math.max(event.clientX + this.productImagePreviewOffsetPx, viewportPadding),
      maxLeft
    );
    this.hoveredProductImagePreviewTop = Math.min(
      Math.max(event.clientY + this.productImagePreviewOffsetPx, viewportPadding),
      maxTop
    );
  }

  ngOnDestroy() {
    for (const row of this.supplierPriceRequests.keys()) this.cancelSupplierRetailPrice(row);
    this.automaticSupplyDisposed = true;
    this.clearAutomaticSupplySuggestions();
    this.destroyed = true;
    if (this.focusTimer) clearTimeout(this.focusTimer);
    this.productDialogSubscriptions.unsubscribe();
    this.routeSubscription.unsubscribe();
    this.storeSubscription.unsubscribe();
    this.novaPoshtaWarehouseLookupSubscription?.unsubscribe();
    this.warehouseContextSubscription?.unsubscribe();
    this.warehouseOptionsVersion++;
  }

  private uuid() {
    const values = new Uint8Array(16);
    window.crypto.getRandomValues(values);
    values[6] = (values[6] & 0x0f) | 0x40;
    values[8] = (values[8] & 0x3f) | 0x80;
    const hex = Array.from(values, (value) => value.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
      16,
      20
    )}-${hex.slice(20)}`;
  }
}
